import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { streamAI } from "@/lib/ai";
import { useFacility } from "@/context/FacilityContext";
import { printDocument } from "@/lib/print";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  ArrowLeft, Printer, Loader2, User, FileClock, Activity, Search, HeartPulse,
  ReceiptText, LogOut, MessageSquare, Plus, Network, FlaskConical, Scan, Send, X,
  Upload, FolderOpen, AlertTriangle, ArrowUp, ArrowDown, Eye,
  Copy, ChevronLeft, ChevronRight, ChevronDown, Clock, FileText, CheckCircle2, Stethoscope, Scissors,
  ClipboardList, ScrollText, Trash2, Sparkles, Pencil, Check, Download,
} from "lucide-react";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const TABS = [
  { key: "registration", label: "Registration", icon: User },
  { key: "history", label: "Patient History", icon: ClipboardList },
  { key: "assessment", label: "Present Assessment", icon: ScrollText },
  { key: "notes", label: "Admission Notes", icon: FileClock },
  { key: "vitals", label: "Vitals Chart", icon: Activity },
  { key: "investigations", label: "Investigations", icon: Search },
  { key: "treatment", label: "Treatment", icon: HeartPulse },
  { key: "ot", label: "OT Management", icon: Scissors },
  { key: "charges", label: "Charge Sheet", icon: ReceiptText },
  { key: "consultant", label: "Consultant Visits", icon: Stethoscope },
  { key: "invoice", label: "Invoice", icon: FileText },
  { key: "discharge", label: "Discharge", icon: LogOut },
  { key: "chat", label: "Chat", icon: MessageSquare },
];

const htmlEsc = (v) => (v == null ? "" : String(v)).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const nlBr = (v) => htmlEsc(v).replace(/\n/g, "<br/>");
function sectionHeader(adm) {
  const p = adm.patient || {};
  return `<div class="row"><div><h1>${htmlEsc(p.name)}</h1><div class="muted">${htmlEsc(adm.ipd_no)} · ${htmlEsc(p.uhid)} · Bed ${htmlEsc(adm.bed_number)} · ${htmlEsc(adm.department)}</div></div><div class="muted" style="text-align:right">${new Date().toLocaleDateString()}<br/>Day ${htmlEsc(adm.days)}</div></div><hr class="hr"/>`;
}
function printSection(adm, title, inner) {
  printDocument(title, sectionHeader(adm) + inner);
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400">{label}</div>
      <div className="text-sm text-slate-800 mt-0.5">{value || <span className="text-slate-400">Not recorded</span>}</div>
    </div>
  );
}

export default function AdmissionDetail() {
  const { admissionId } = useParams();
  const navigate = useNavigate();
  const { has } = useFacility();
  const editable = has("ipd:edit");
  const [adm, setAdm] = useState(null);
  const [tab, setTab] = useState("registration");
  const [busy, setBusy] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [doctors, setDoctors] = useState([]);

  const load = useCallback(() => {
    api.get(`/ipd/admissions/${admissionId}`).then((r) => setAdm(r.data)).catch(() => toast.error("Failed to load admission"));
  }, [admissionId]);
  useEffect(load, [load]);
  useEffect(() => { api.get("/ipd/charge-catalog").then((r) => setCatalog(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/ipd/meta").then((r) => setDoctors(r.data?.doctors || [])).catch(() => {}); }, []);

  const addEntry = async (section, data) => {
    setBusy(true);
    try { await api.post(`/ipd/admissions/${admissionId}/entry`, { section, data }); load(); return true; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return false; }
    finally { setBusy(false); }
  };

  const editCharge = async (chargeId, data) => {
    try { await api.patch(`/ipd/admissions/${admissionId}/charges/${chargeId}`, data); toast.success("Charge updated"); load(); return true; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return false; }
  };
  const deleteCharge = async (c) => {
    if (!window.confirm(`Delete charge "${c.description}" (₹${Number(c.amount || 0).toLocaleString()})? This cannot be undone.`)) return;
    try { await api.delete(`/ipd/admissions/${admissionId}/charges/${c.id}`); toast.success("Charge removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openCareCompare = () => window.open("https://carecompare.in", "_blank", "noopener,noreferrer");
  const saveOt = async (section, data) => {
    setBusy(true);
    try { await api.post(`/ipd/admissions/${admissionId}/ot`, { section, data }); load(); toast.success("OT record saved"); return true; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return false; }
    finally { setBusy(false); }
  };
  const savePatientHistory = async (data) => {
    setBusy(true);
    try { await api.put(`/ipd/admissions/${admissionId}/patient-history`, data); load(); toast.success("Patient history saved"); return true; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return false; }
    finally { setBusy(false); }
  };
  const savePresentAssessment = async (data) => {
    setBusy(true);
    try { await api.put(`/ipd/admissions/${admissionId}/present-assessment`, data); load(); toast.success("Present assessment saved"); return true; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return false; }
    finally { setBusy(false); }
  };
  const [downloadingCaseFile, setDownloadingCaseFile] = useState(false);

  if (!adm) return <div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;
  const p = adm.patient || {};
  const discharged = adm.status === "discharged";
  const settledLock = adm.invoice_status === "settled";
  const canCorrect = has("ipd:delete") && !settledLock;

  const catalogCharges = (adm.charges || []).filter((c) => c.kind !== "consultant");
  const consultantVisits = (adm.charges || []).filter((c) => c.kind === "consultant");
  const chargesByDay = catalogCharges.reduce((acc, c) => {
    const d = (c.at || "").slice(0, 10) || "—";
    (acc[d] = acc[d] || []).push(c);
    return acc;
  }, {});
  const chargeDays = Object.keys(chargesByDay).sort();

  const generateInvoice = async (status) => {    setBusy(true);
    try {
      await api.post(`/ipd/admissions/${admissionId}/generate-invoice`, { status });
      toast.success(status === "settled" ? "Final bill generated" : "Invoice kept pending");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const printFile = () => {
    printDocument("IPD Case File", `
      <div class="row"><div><h1>${p.name || ""}</h1><div class="muted">${adm.ipd_no} · Bed ${adm.bed_number} · ${adm.department || ""}</div></div>
      <div class="muted" style="text-align:right">${new Date().toLocaleDateString()}<br/>Day ${adm.days}</div></div>
      <hr class="hr"/>
      <div class="sec"><h3>Diagnosis</h3>${adm.reason || "—"}</div>
      <div class="sec"><h3>Admitting doctor</h3>${adm.admitting_doctor_name || "Unassigned"}</div>`);
  };

  const printDischarge = () => printSection(adm, "Discharge Summary",
    `<div class="sec"><h3>Discharged on</h3>${adm.discharged_at ? new Date(adm.discharged_at).toLocaleString() : "—"}</div>
     <div class="sec"><h3>Summary</h3>${nlBr(adm.discharge_summary) || "No summary recorded."}</div>`);

  const downloadCaseFile = async () => {
    setDownloadingCaseFile(true);
    try {
      const res = await api.get(`/ipd/admissions/${admissionId}/case-file.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `case-file-${adm.ipd_no || admissionId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not generate case file PDF");
    } finally { setDownloadingCaseFile(false); }
  };

  return (
    <div data-testid="admission-detail-page">
      <button onClick={() => navigate("/app/ipd/admissions")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-4" data-testid="back-to-admissions"><ArrowLeft className="h-4 w-4" /> Back to admissions</button>

      <div className="bg-slate-900 text-white rounded-md p-5 mb-6 flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-display text-lg font-semibold">{p.name}</span>
            <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${discharged ? "bg-slate-700 text-slate-300" : "bg-emerald-500/20 text-emerald-300"}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${discharged ? "bg-slate-400" : "bg-emerald-400"}`} />{adm.status}
            </span>
          </div>
          <div className="text-sm text-slate-400 mt-0.5">
            <span className="font-mono">{adm.ipd_no}</span> · {p.uhid} · {p.gender || "—"}{p.age ? `, ${p.age}y` : ""} · {adm.department || "—"} / Bed {adm.bed_number}
            {p.allergies ? <span className="ml-2 text-red-300">⚠ {p.allergies}</span> : ""}
          </div>
          <div className="text-xs text-slate-500 mt-1">Consultant: {adm.admitting_doctor_name || "Unassigned"} · Diagnosis: {adm.reason || "—"}</div>
        </div>
        <div className="text-right text-sm"><div className="text-slate-400 text-xs">Length of stay</div>Day {adm.days}</div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={downloadCaseFile} disabled={downloadingCaseFile} className="bg-transparent text-white border-slate-600 hover:bg-slate-800" data-testid="download-case-file">{downloadingCaseFile ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />} Case File PDF</Button>
          <Button variant="outline" onClick={printFile} className="bg-transparent text-white border-slate-600 hover:bg-slate-800" data-testid="print-ipd-file"><Printer className="h-4 w-4 mr-1.5" /> Print</Button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <aside className="lg:w-56 shrink-0">
          <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible" data-testid="ipd-file-tabs">
            {TABS.map((t) => (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`ipd-tab-${t.key}`}
                className={`flex items-center gap-2.5 px-3 py-2 rounded-md text-sm whitespace-nowrap text-left transition-colors ${
                  tab === t.key ? "bg-blue-50 text-blue-700 font-medium border border-blue-200" : "text-slate-600 hover:bg-slate-100 border border-transparent"}`}>
                <t.icon className="h-4 w-4" /> {t.label}
              </button>
            ))}
          </nav>
        </aside>

        <div className="flex-1 min-w-0">
          {tab === "registration" && (
            <div className="space-y-5" data-testid="tab-panel-registration">
              <div className="bg-white border border-slate-200 rounded-md p-6">
                <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-4">Patient Identity</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
                  <Field label="Full name" value={p.name} />
                  <Field label="UHID" value={p.uhid} />
                  <Field label="Age / Gender" value={p.age ? `${p.age} yrs / ${p.gender || "—"}` : (p.gender || null)} />
                  <Field label="Blood group" value={p.blood_group} />
                  <Field label="Date of birth" value={p.dob} />
                  <Field label="Mobile" value={p.mobile} />
                </div>
              </div>
              <div className="bg-white border border-slate-200 rounded-md p-6">
                <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-4">Clinical Background</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
                  <Field label="Allergies" value={p.allergies} />
                  <Field label="Address" value={p.address} />
                  <Field label="Emergency contact" value={p.emergency_contact_name ? `${p.emergency_contact_name} · ${p.emergency_contact_phone || ""}` : null} />
                  <Field label="Reason for admission" value={adm.reason} />
                  <Field label="Admitted on" value={adm.admitted_date} />
                  <Field label="Bed type" value={adm.bed_type} />
                </div>
              </div>
            </div>
          )}

          {tab === "history" && (
            <PatientHistoryPanel history={p.history} adm={adm} editable={editable} busy={busy} onSave={savePatientHistory} />
          )}

          {tab === "assessment" && (
            <PresentAssessmentPanel assessment={adm.present_assessment} adm={adm} editable={editable && !discharged} busy={busy} onSave={savePresentAssessment} />
          )}

          {tab === "notes" && (
            <NotesPanel notes={adm.clinical_notes} adm={adm} editable={editable} busy={busy} onAdd={(data) => addEntry("clinical_notes", data)} />
          )}

          {tab === "vitals" && (
            <VitalsPanel vitals={adm.vitals} adm={adm} editable={editable} busy={busy} onAdd={(v) => addEntry("vitals", v)} />
          )}

          {tab === "investigations" && (
            <InvestigationsPanel adm={adm} onEntry={addEntry} editable={editable} busy={busy} />
          )}

          {tab === "treatment" && (
            <TreatmentPanel adm={adm} onEntry={(data) => addEntry("treatment", data)} editable={editable} busy={busy} />
          )}

          {tab === "ot" && (
            <OTPanel ot={adm.ot} doctors={doctors} editable={editable && !discharged} busy={busy} onSave={saveOt} />
          )}

          {tab === "charges" && (
            <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="tab-panel-charges">
              <div className="flex items-center justify-between mb-4">
                <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400">Charge Sheet {discharged ? "(final)" : "(running)"}</div>
                <div className="text-sm font-semibold text-slate-800">Total Charges: <span data-testid="charges-total">₹{adm.charges_total.toLocaleString()}</span></div>
              </div>

              {settledLock && (
                <div className="mb-4 text-[11px] text-slate-500 bg-slate-50 border border-slate-100 rounded-md px-3 py-2" data-testid="charges-locked-note">This bill is finalised (settled) — charge lines are locked. Reopening a settled bill is not available yet.</div>
              )}
              {editable && !discharged && (
                <div className="mb-5 bg-slate-50 border border-slate-100 rounded-md p-4" data-testid="add-charge-block">
                  <Label className="text-xs text-slate-500">Add a charge from the hospital list</Label>
                  <div className="mt-1.5">
                    <CatalogChargeForm catalog={catalog} busy={busy} onAdd={(item, qty) => addEntry("charges", { catalog_id: item.id, description: qty > 1 ? `${item.name} × ${qty}` : item.name, category: item.category, unit_amount: item.amount, qty, amount: item.amount * qty })} />
                  </div>
                </div>
              )}

              {/* Bed charges (per day) */}
              <div className="border border-slate-100 rounded-md overflow-hidden mb-4" data-testid="bed-charges-group">
                <div className="bg-slate-50 px-4 py-2 text-xs font-semibold text-slate-600 flex justify-between">
                  <span>Bed Charges — {adm.bed_type} · {adm.days} day(s) × ₹{adm.bed_charge_daily}</span>
                  <span>₹{adm.bed_charges_total.toLocaleString()}</span>
                </div>
              </div>

              {/* Day-wise catalog charges */}
              {chargeDays.length === 0 ? (
                <div className="text-sm text-slate-400 py-6 text-center border border-dashed border-slate-200 rounded-md">No charges added yet.</div>
              ) : (
                <div className="space-y-4">
                  {chargeDays.map((d) => {
                    const list = chargesByDay[d];
                    const daySum = list.reduce((s, c) => s + Number(c.amount || 0), 0);
                    return (
                      <div key={d} className="border border-slate-100 rounded-md overflow-hidden" data-testid={`charge-day-${d}`}>
                        <div className="bg-slate-50 px-4 py-2 text-xs font-semibold text-slate-600 flex justify-between">
                          <span>{d === "—" ? "Undated" : new Date(d).toLocaleDateString([], { weekday: "short", day: "2-digit", month: "short", year: "numeric" })}</span>
                          <span>₹{daySum.toLocaleString()}</span>
                        </div>
                        <Table>
                          <TableBody>
                            {list.map((c) => (
                              <CatalogChargeRow key={c.id} c={c} canEdit={false} onEdit={editCharge} onDelete={deleteCharge} />
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {tab === "consultant" && (
            <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="tab-panel-consultant">
              <div className="flex items-center justify-between mb-4">
                <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400">Consultant Visit Sheet</div>
                <div className="text-sm font-semibold text-slate-800">Consultant Charges: <span data-testid="consultant-total">₹{consultantVisits.reduce((s, c) => s + Number(c.amount || 0), 0).toLocaleString()}</span></div>
              </div>
              {editable && !discharged && (
                <div className="mb-5 bg-slate-50 border border-slate-100 rounded-md p-4" data-testid="add-consultant-visit-block">
                  <Label className="text-xs text-slate-500">Record a consultant visit</Label>
                  <div className="mt-1.5">
                    <ConsultantVisitForm doctors={doctors} busy={busy} onAdd={(consultant, amount, note) => addEntry("charges", { kind: "consultant", consultant_id: consultant.id, consultant_name: consultant.name, description: `${consultant.name}${note ? ` — ${note}` : " — consultant visit"}`, category: "Consultant Visit", amount })} />
                  </div>
                  <p className="text-[11px] text-slate-400 mt-2">Each consultant visit is added to the final invoice automatically under the consultant's name.</p>
                </div>
              )}
              {consultantVisits.length === 0 ? (
                <div className="text-sm text-slate-400 py-6 text-center border border-dashed border-slate-200 rounded-md">No consultant visits recorded yet.</div>
              ) : (
                <Table>
                  <TableHeader><TableRow className="bg-slate-50"><TableHead>Date</TableHead><TableHead>Consultant</TableHead><TableHead>Note</TableHead><TableHead className="text-right">Charge (₹)</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {consultantVisits.map((c) => (
                      <ConsultantChargeRow key={c.id} c={c} canEdit={false} onEdit={editCharge} onDelete={deleteCharge} />
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          )}

          {tab === "invoice" && (
            <div className="space-y-5 max-w-2xl" data-testid="tab-panel-invoice">
              <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="invoice-card">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="font-semibold text-slate-800">Invoice</div>
                    {adm.invoice?.invoice_no && <div className="text-xs font-mono text-blue-700" data-testid="invoice-number">{adm.invoice.invoice_no}</div>}
                  </div>
                  <div className="flex items-center gap-2">
                    <InvoiceStatusBadge status={adm.invoice_status} />
                    <Button variant="outline" size="sm" onClick={() => printInvoice(adm)} data-testid="invoice-print-btn"><Printer className="h-4 w-4 mr-1.5" /> Print</Button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  {Object.entries(adm.category_totals || {}).map(([c, v]) => (
                    <div key={c} className="flex justify-between text-sm" data-testid={`invoice-cat-${c.replace(/[^a-zA-Z]/g, "").toLowerCase()}`}><span className="text-slate-600">{c}</span><span className="text-slate-800">₹{Number(v).toLocaleString()}</span></div>
                  ))}
                  {consultantVisits.length > 0 && (
                    <div className="mt-1 pl-3 border-l-2 border-slate-100 space-y-1" data-testid="invoice-consultant-breakdown">
                      <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400">Consultant visits</div>
                      {consultantVisits.map((c) => (
                        <div key={c.id} className="flex justify-between text-xs text-slate-500"><span>{c.consultant_name}{c.at ? ` · ${new Date(c.at).toLocaleDateString([], { day: "2-digit", month: "short" })}` : ""}</span><span>₹{Number(c.amount || 0).toLocaleString()}</span></div>
                      ))}
                    </div>
                  )}
                  <div className="flex justify-between text-sm font-medium border-t border-slate-100 pt-2 mt-2"><span>Subtotal</span><span data-testid="invoice-subtotal">₹{adm.charges_total.toLocaleString()}</span></div>
                  {adm.discount > 0 && <div className="flex justify-between text-sm text-amber-700"><span>Discount</span><span data-testid="invoice-discount">− ₹{Number(adm.discount).toLocaleString()}</span></div>}
                  <div className="flex justify-between text-sm font-semibold"><span>Total</span><span data-testid="invoice-total">₹{Number(adm.net_total).toLocaleString()}</span></div>
                  <div className="flex justify-between text-sm text-emerald-700"><span>Paid</span><span data-testid="invoice-paid">₹{Number(adm.paid_total || 0).toLocaleString()}</span></div>
                  <div className="flex justify-between text-base font-bold text-blue-800 bg-blue-50 rounded px-2 py-1.5"><span>Balance Due</span><span data-testid="invoice-balance">₹{Number(adm.balance || 0).toLocaleString()}</span></div>
                </div>
              </div>

              {canCorrect && (catalogCharges.length > 0 || consultantVisits.length > 0) && (
                <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="invoice-charge-editor">
                  <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-1">Adjust Charge Lines (Admin)</div>
                  <p className="text-[11px] text-slate-400 mb-3">Edit or remove individual staff-added charges below. Totals recalculate automatically. Auto bed charges, discounts and payments are not editable here.</p>
                  {catalogCharges.length > 0 && (
                    <div className="border border-slate-100 rounded-md overflow-hidden mb-3">
                      <div className="bg-slate-50 px-4 py-2 text-xs font-semibold text-slate-600">Charges</div>
                      <Table><TableBody>
                        {catalogCharges.map((c) => <CatalogChargeRow key={c.id} c={c} canEdit={true} onEdit={editCharge} onDelete={deleteCharge} />)}
                      </TableBody></Table>
                    </div>
                  )}
                  {consultantVisits.length > 0 && (
                    <div className="border border-slate-100 rounded-md overflow-hidden">
                      <div className="bg-slate-50 px-4 py-2 text-xs font-semibold text-slate-600">Consultant visits</div>
                      <Table><TableBody>
                        {consultantVisits.map((c) => <ConsultantChargeRow key={c.id} c={c} canEdit={true} onEdit={editCharge} onDelete={deleteCharge} />)}
                      </TableBody></Table>
                    </div>
                  )}
                </div>
              )}
              {settledLock && (catalogCharges.length > 0 || consultantVisits.length > 0) && (
                <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-100 rounded-md px-3 py-2" data-testid="invoice-charges-locked-note">This bill is finalised (settled) — charge lines are locked and can no longer be edited.</div>
              )}

              {editable && adm.invoice_status !== "settled" && (
                <>
                  <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="invoice-discount-card">
                    <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-3">Discount</div>
                    <DiscountForm current={adm.discount || 0} busy={busy} onSave={async (amount) => {
                      setBusy(true);
                      try { await api.patch(`/ipd/admissions/${admissionId}/discount`, { discount: amount }); toast.success("Discount updated"); load(); return true; }
                      catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return false; }
                      finally { setBusy(false); }
                    }} />
                  </div>

                  <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="invoice-payment-card">
                    <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-3">Record Payment</div>
                    <PaymentForm busy={busy} onAdd={(amount, mode, note) => addEntry("payments", { amount, mode, note })} />
                    {(adm.payments || []).length > 0 && (
                      <div className="mt-4 space-y-1.5" data-testid="invoice-payments-list">
                        {adm.payments.map((p) => (
                          <div key={p.id} className="flex justify-between text-sm text-slate-600 border-b border-slate-50 pb-1"><span>{p.mode}{p.note ? ` · ${p.note}` : ""} <span className="text-[11px] text-slate-400">{p.at ? new Date(p.at).toLocaleDateString() : ""}</span></span><span className="text-emerald-700">₹{Number(p.amount || 0).toLocaleString()}</span></div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-3 justify-end">
                    <Button variant="outline" disabled={busy} onClick={() => generateInvoice("pending")} data-testid="invoice-keep-pending-btn"><Clock className="h-4 w-4 mr-1.5" /> Keep Pending</Button>
                    <Button disabled={busy || Number(adm.balance) > 0} onClick={() => generateInvoice("settled")} className="bg-emerald-600 hover:bg-emerald-700" data-testid="invoice-generate-btn"><CheckCircle2 className="h-4 w-4 mr-1.5" /> Generate Final Bill</Button>
                  </div>
                  {Number(adm.balance) > 0 && <p className="text-[11px] text-slate-400 text-right">Final bill can be generated once the balance is fully paid, or keep it pending to collect later.</p>}
                </>
              )}
              {adm.invoice_status === "settled" && <div className="text-sm text-emerald-700 flex items-center gap-1.5" data-testid="invoice-settled-note"><CheckCircle2 className="h-4 w-4" /> Final bill generated and settled.</div>}
            </div>
          )}

          {tab === "discharge" && (
            <div className="space-y-5 max-w-2xl" data-testid="tab-panel-discharge">
              <div className="bg-white border border-slate-200 rounded-md p-6 space-y-4">
                <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400">Discharge</div>
                {discharged ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-emerald-700 font-medium">Discharged on {adm.discharged_at ? new Date(adm.discharged_at).toLocaleString() : "—"}</div>
                      <Button variant="outline" size="sm" onClick={printDischarge} data-testid="print-discharge"><Printer className="h-4 w-4 mr-1.5" /> Print</Button>
                    </div>
                    <div className="text-sm text-slate-700 whitespace-pre-wrap">{adm.discharge_summary || "No summary recorded."}</div>
                  </div>
                ) : (
                  <DischargeForm editable={editable} busy={busy} admissionId={admissionId} facilityId={adm.facility_id} onDischarge={async (summary) => {
                    setBusy(true);
                    try { await api.patch(`/ipd/admissions/${admissionId}/discharge`, { discharge_summary: summary }); toast.success("Patient discharged — final invoice generated"); load(); }
                    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                    finally { setBusy(false); }
                  }} />
                )}
              </div>
              {discharged && adm.invoice && (
                <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="discharge-invoice-card">
                  <div className="flex items-center justify-between mb-4">
                    <div><div className="font-semibold text-slate-800">Final Invoice</div><div className="text-xs font-mono text-blue-700">{adm.invoice.invoice_no}</div></div>
                    <div className="flex items-center gap-2"><InvoiceStatusBadge status={adm.invoice.status || adm.invoice_status} /><Button variant="outline" size="sm" onClick={() => printInvoice(adm)} data-testid="print-invoice-btn2"><Printer className="h-4 w-4 mr-1.5" /> Print Invoice</Button></div>
                  </div>
                  <div className="space-y-1.5">
                    {Object.entries(adm.invoice.category_totals || {}).map(([c, v]) => (
                      <div key={c} className="flex justify-between text-sm"><span className="text-slate-600">{c}</span><span className="text-slate-800">₹{Number(v).toLocaleString()}</span></div>
                    ))}
                    {adm.invoice.discount > 0 && <div className="flex justify-between text-sm text-amber-700"><span>Discount</span><span>− ₹{Number(adm.invoice.discount).toLocaleString()}</span></div>}
                    <div className="flex justify-between text-sm font-semibold border-t border-slate-100 pt-2 mt-2"><span>Total</span><span>₹{Number(adm.invoice.total).toLocaleString()}</span></div>
                    <div className="flex justify-between text-sm text-emerald-700"><span>Paid</span><span>₹{Number(adm.invoice.paid).toLocaleString()}</span></div>
                    <div className="flex justify-between text-base font-bold text-blue-800 bg-blue-50 rounded px-2 py-1.5"><span>Balance Due</span><span>₹{Number(adm.invoice.balance).toLocaleString()}</span></div>
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === "chat" && (
            <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="tab-panel-chat">
              <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-4">Care Team Chat</div>
              <div className="space-y-3 max-h-96 overflow-y-auto thin-scroll mb-4">
                {(adm.chat || []).length === 0 ? <div className="text-sm text-slate-400">No messages yet.</div> :
                  adm.chat.map((m) => (
                    <div key={m.id} className="bg-slate-50 border border-slate-100 rounded-md px-3 py-2">
                      <div className="text-sm text-slate-800">{m.text}</div>
                      <div className="text-[11px] text-slate-400 mt-0.5">{m.by} · {new Date(m.at).toLocaleString()}</div>
                    </div>
                  ))}
              </div>
              <ChatForm onSend={(text) => addEntry("chat", { text })} busy={busy} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ListSection({ testid, title, items, render, form, empty, editable }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-6" data-testid={`tab-panel-${testid}`}>
      <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-4">{title}</div>
      {editable && form}
      <div className="mt-4 space-y-2">
        {(items || []).length === 0 ? <div className="text-sm text-slate-400">{empty}</div> :
          [...items].reverse().map((it) => <div key={it.id} className="border border-slate-100 rounded-md px-3 py-2">{render(it)}</div>)}
      </div>
    </div>
  );
}

const NOTE_TYPES = ["Duty Doctor", "Consultant", "History & Initial Assessment", "Nursing Note", "OT Notes", "Procedure Note"];
const noteSlug = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

function NoteChip({ active, onClick, label, count, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs border transition-colors ${
        active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
      {label}<span className={`text-[10px] ${active ? "text-slate-300" : "text-slate-400"}`}>{count}</span>
    </button>
  );
}

function NotesPanel({ notes, adm, onAdd, editable, busy }) {
  const [type, setType] = useState(NOTE_TYPES[0]);
  const [text, setText] = useState("");
  const [filter, setFilter] = useState("All");
  const list = notes || [];
  const counts = NOTE_TYPES.reduce((a, t) => { a[t] = list.filter((n) => n.note_type === t).length; return a; }, {});
  const filtered = filter === "All" ? list : list.filter((n) => n.note_type === filter);
  const printNotes = () => {
    const rows = [...filtered].reverse().map((n) => `<div class="sec"><h3>${htmlEsc(n.note_type || "Note")} — ${htmlEsc(n.by || "")} · ${n.at ? new Date(n.at).toLocaleString() : ""}</h3>${nlBr(n.text)}</div>`).join("") || "<div>No notes.</div>";
    printSection(adm, "Admission Notes", `<div class="muted">Filter: ${htmlEsc(filter)}</div>${rows}`);
  };
  return (
    <div className="space-y-5" data-testid="tab-panel-notes">
      <div className="flex justify-end"><Button variant="outline" size="sm" onClick={printNotes} data-testid="print-notes"><Printer className="h-4 w-4 mr-1.5" /> Print</Button></div>
      {editable && (
        <div className="bg-white border border-slate-200 rounded-md p-6 space-y-4">
          <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400">Add Clinical Note</div>
          <div className="space-y-1.5"><Label className="text-xs">Note type</Label>
            <select className="w-full sm:w-80 h-9 rounded-md border border-slate-300 px-3 text-sm bg-white" value={type} onChange={(e) => setType(e.target.value)} data-testid="note-type-select">
              {NOTE_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <Textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} placeholder={`Write the ${type.toLowerCase()}…`} data-testid="note-input" />
          <div className="flex justify-end"><Button size="sm" disabled={busy || !text.trim()} onClick={async () => { if (await onAdd({ text: text.trim(), note_type: type })) setText(""); }} className="bg-blue-700 hover:bg-blue-800" data-testid="note-add"><Plus className="h-4 w-4 mr-1.5" /> Add note</Button></div>
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-md p-6">
        <div className="flex flex-wrap gap-2 mb-4">
          <NoteChip active={filter === "All"} onClick={() => setFilter("All")} label="All" count={list.length} testid="note-filter-all" />
          {NOTE_TYPES.map((t) => <NoteChip key={t} active={filter === t} onClick={() => setFilter(t)} label={t} count={counts[t]} testid={`note-filter-${noteSlug(t)}`} />)}
        </div>
        <div className="space-y-2">
          {filtered.length === 0 ? <div className="text-sm text-slate-400">No notes in this category yet.</div> :
            [...filtered].reverse().map((n) => (
              <div key={n.id} className="border border-slate-100 rounded-md px-3 py-2.5" data-testid={`note-item-${n.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="inline-flex items-center text-[11px] font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-0.5">{n.note_type || "Note"}</span>
                  <span className="text-[11px] text-slate-400">{n.by} · {new Date(n.at).toLocaleString()}</span>
                </div>
                <div className="text-sm text-slate-800 whitespace-pre-wrap mt-1.5">{n.text}</div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

const STD_VITALS = [
  { key: "hr", label: "HR", unit: "bpm", color: "#ef4444", trend: "Heart Rate", low: 60, high: 100 },
  { key: "sbp", label: "SBP", unit: "mmHg", color: "#3b82f6", trend: "Sys BP", low: 90, high: 140 },
  { key: "dbp", label: "DBP", unit: "mmHg", color: "#8b5cf6", trend: "Dia BP", low: 60, high: 90 },
  { key: "spo2", label: "SpO₂", unit: "%", color: "#0ea5e9", trend: "SpO₂", low: 95, high: 100 },
  { key: "temp", label: "Temp", unit: "°C", color: "#f59e0b", trend: "Temp", low: 36, high: 37.5 },
  { key: "rr", label: "RR", unit: "/min", color: "#10b981", trend: "Resp Rate", low: 12, high: 20 },
  { key: "gcs", label: "GCS", unit: "/15", color: "#8b5cf6", trend: "GCS", low: 13, high: 15 },
  { key: "uo", label: "UO", unit: "mL", color: "#06b6d4", trend: "Urine Output" },
];
const STD_KEYS = STD_VITALS.map((v) => v.key);
const RESERVED = new Set(["id", "by", "at", ...STD_KEYS]);
const CUSTOM_PALETTE = ["#ec4899", "#84cc16", "#f97316", "#a855f7", "#eab308", "#14b8a6"];
const TREND_KEYS = ["hr", "sbp", "dbp", "spo2", "temp", "rr"];
const vitalMeta = (key) => STD_VITALS.find((v) => v.key === key);
const isAbnormal = (meta, val) => {
  if (!meta) return false;
  const n = parseFloat(val);
  if (isNaN(n)) return false;
  if (meta.low != null && n < meta.low) return true;
  if (meta.high != null && n > meta.high) return true;
  return false;
};
const cellClass = (key, val) => {
  const meta = vitalMeta(key);
  if (!isAbnormal(meta, val)) return "text-slate-700";
  return key === "temp" ? "text-amber-600 font-semibold" : "text-red-600 font-semibold";
};
const slugKey = (s) => s.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/(^_|_$)/g, "");

const LAB_PANEL = [
  { k: "Hb", unit: "g/dL", low: 12, high: 17, cLow: 7 },
  { k: "TLC", unit: "/µL", low: 4000, high: 11000, cHigh: 20000 },
  { k: "DLC", unit: "%" },
  { k: "Platelets", unit: "/µL", low: 150000, high: 410000, cLow: 50000 },
  { k: "Blood Urea", unit: "mg/dL", low: 7, high: 20, cHigh: 100 },
  { k: "S. Creatinine", unit: "mg/dL", low: 0.6, high: 1.3, cHigh: 5 },
  { k: "Sodium", unit: "mEq/L", low: 135, high: 145, cLow: 120, cHigh: 160 },
  { k: "Potassium", unit: "mEq/L", low: 3.5, high: 5.1, cLow: 2.5, cHigh: 6.5 },
  { k: "S. Bilirubin", unit: "mg/dL", low: 0.1, high: 1.2 },
  { k: "AST/ALT", unit: "U/L" },
  { k: "PT (C/T)", unit: "sec" },
  { k: "PTTK (C/T)", unit: "sec" },
  { k: "INR", low: 0.8, high: 1.2, cHigh: 4 },
  { k: "S. Protein", unit: "g/dL" },
  { k: "S. Albumin", unit: "g/dL", low: 3.5, high: 5.5 },
  { k: "S. Procalcitonin", unit: "ng/mL", high: 0.5, cHigh: 2 },
  { k: "ABG", unit: "mmHg" },
  { k: "Blood Culture" },
  { k: "CBC" },
  { k: "Chest X-Ray PA" },
  { k: "ECG" },
  { k: "Procalcitonin", unit: "ng/mL", high: 0.5, cHigh: 2 },
  { k: "S. Electrolytes", unit: "mEq/L" },
];
const RAD_TYPES = ["CT Scan", "MRI", "X-Ray", "Ultrasound", "PET Scan", "Mammography", "Other"];
const DOC_CATEGORIES = ["Blood Culture", "ECG", "Biopsy Report", "Histopathology", "Discharge Summary", "Referral Letter", "Other"];

const labFlag = (p, raw) => {
  const n = parseFloat(raw);
  if (isNaN(n)) return null;
  if ((p.cLow != null && n < p.cLow) || (p.cHigh != null && n > p.cHigh)) return "critical";
  if (p.high != null && n > p.high) return "high";
  if (p.low != null && n < p.low) return "low";
  return null;
};
const labRange = (p) => {
  if (p.low != null && p.high != null) return `Normal: ${p.low}–${p.high}${p.unit ? " " + p.unit : ""}`;
  if (p.high != null) return `Normal: <${p.high}${p.unit ? " " + p.unit : ""}`;
  return p.unit ? `Unit: ${p.unit}` : "No reference range";
};
const fmtLabDate = (d) => { try { return new Date(d).toLocaleDateString([], { day: "2-digit", month: "short" }); } catch { return d; } };

async function uploadIpdFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/ipd/files", fd);
  return data;
}
async function viewIpdFile(id) {
  const res = await api.get(`/ipd/files/${id}`, { responseType: "blob" });
  window.open(URL.createObjectURL(res.data), "_blank", "noopener,noreferrer");
}

function InvestigationsPanel({ adm, onEntry, editable, busy }) {
  const labResults = adm.lab_results || [];
  const radiology = adm.radiology || [];
  const documents = adm.documents || [];
  const [labOpen, setLabOpen] = useState(false);
  const [radOpen, setRadOpen] = useState(false);
  const [docOpen, setDocOpen] = useState(false);
  const dates = [...new Set(labResults.map((r) => r.date).filter(Boolean))].sort();
  const panelKeys = new Set(LAB_PANEL.map((p) => p.k));
  const extraParams = [...new Set(labResults.flatMap((r) => Object.keys(r.values || {})))]
    .filter((k) => !panelKeys.has(k)).map((k) => ({ k }));
  const allParams = [...LAB_PANEL, ...extraParams];
  const valueFor = (param, d) => { const e = labResults.find((r) => r.date === d); return e?.values?.[param]; };
  const openCareCompare = () => window.open("https://carecompare.in", "_blank", "noopener,noreferrer");

  const printLab = () => {
    const cols = dates.length ? dates : [];
    const head = `<tr><th style="text-align:left">Investigation</th>${cols.map((d) => `<th>${fmtLabDate(d)}</th>`).join("")}</tr>`;
    const rows = allParams.map((p) => `<tr><td style="text-align:left">${p.k}</td>${cols.map((d) => `<td style="text-align:center">${valueFor(p.k, d) ?? "—"}</td>`).join("")}</tr>`).join("");
    printDocument("Lab Results", `<div class="row"><div><h1>${adm.patient?.name || ""}</h1><div class="muted">${adm.ipd_no} · Bed ${adm.bed_number} · ${adm.department || ""}</div></div><div class="muted" style="text-align:right">${new Date().toLocaleDateString()}</div></div><hr class="hr"/><table style="width:100%;border-collapse:collapse" border="1" cellpadding="6"><thead>${head}</thead><tbody>${rows}</tbody></table>`);
  };

  const renderCell = (p, d) => {
    const raw = valueFor(p.k, d);
    if (raw == null || raw === "") return <span className="text-slate-300">—</span>;
    const f = labFlag(p, raw);
    const cls = f === "critical" ? "text-red-600 font-semibold" : f === "high" ? "text-amber-600 font-semibold" : f === "low" ? "text-blue-600 font-semibold" : "text-slate-700";
    return (
      <span className={cls} title={labRange(p)}>
        {raw}{p.unit ? <span className="text-[10px] text-slate-400 ml-0.5">{p.unit}</span> : ""}
        {f === "high" ? <ArrowUp className="inline h-3 w-3 ml-0.5" /> : f === "low" ? <ArrowDown className="inline h-3 w-3 ml-0.5" /> : f === "critical" ? <AlertTriangle className="inline h-3 w-3 ml-0.5" /> : ""}
      </span>
    );
  };

  return (
    <div className="space-y-6" data-testid="tab-panel-investigations">
      <div className="bg-white border border-slate-200 rounded-md p-4 flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-700 mr-2"><Network className="h-3.5 w-3.5" /> Order via CareCompare</span>
        <Button variant="outline" size="sm" onClick={openCareCompare} data-testid="ipd-order-lab"><FlaskConical className="h-4 w-4 mr-1.5" /> Order Lab</Button>
        <Button variant="outline" size="sm" onClick={openCareCompare} data-testid="ipd-order-imaging"><Scan className="h-4 w-4 mr-1.5" /> Order Imaging</Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="lab-results-section">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2 text-blue-700 font-bold"><FlaskConical className="h-5 w-5" /> LAB RESULTS</div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={printLab} data-testid="lab-print"><Printer className="h-4 w-4 mr-1.5" /> Print</Button>
            {editable && <Button size="sm" className="bg-blue-700 hover:bg-blue-800" onClick={() => setLabOpen(true)} data-testid="enter-results-btn"><Plus className="h-4 w-4 mr-1.5" /> Enter Results</Button>}
          </div>
        </div>
        <div className="overflow-x-auto border border-slate-100 rounded-md">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 border-b border-slate-200 text-slate-600">
              <th className="text-left py-2.5 px-3 font-semibold whitespace-nowrap">Investigation / Date</th>
              {dates.length === 0 ? <th className="py-2.5 px-3 font-semibold text-slate-300">—</th> : dates.map((d) => <th key={d} className="py-2.5 px-3 font-semibold text-center whitespace-nowrap">{fmtLabDate(d)}</th>)}
            </tr></thead>
            <tbody>
              {allParams.map((p) => (
                <tr key={p.k} className="border-b border-slate-50">
                  <td className="py-2 px-3 text-slate-700 whitespace-nowrap" title={labRange(p)}>{p.k}</td>
                  {dates.length === 0 ? <td className="py-2 px-3 text-center text-slate-300">—</td> :
                    dates.map((d) => <td key={d} className={`py-2 px-3 text-center ${labFlag(p, valueFor(p.k, d)) === "critical" ? "bg-red-50" : ""}`}>{renderCell(p, d)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-full bg-red-50 border border-red-200" /> Critical <AlertTriangle className="h-3 w-3 text-red-600" /></span>
          <span className="inline-flex items-center gap-1 text-amber-600"><ArrowUp className="h-3 w-3" /> High</span>
          <span className="inline-flex items-center gap-1 text-blue-600"><ArrowDown className="h-3 w-3" /> Low</span>
          <span className="text-slate-400">· Hover a value to see reference range</span>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="radiology-section">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2 text-purple-700 font-bold"><Scan className="h-5 w-5" /> RADIOLOGY / IMAGING</div>
          {editable && <Button size="sm" className="bg-blue-700 hover:bg-blue-800" onClick={() => setRadOpen(true)} data-testid="add-radiology-btn"><Plus className="h-4 w-4 mr-1.5" /> Add Radiology Report</Button>}
        </div>
        {radiology.length === 0 ? (
          <div className="text-center py-10 text-slate-400"><Scan className="h-8 w-8 mx-auto mb-2 opacity-40" />No radiology reports yet. Click <b>Add Radiology Report</b> to upload one.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {[...radiology].reverse().map((r) => (
              <div key={r.id} className="border border-slate-200 rounded-md p-4" data-testid={`radiology-item-${r.id}`}>
                <div className="flex items-center justify-between"><span className="text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded px-2 py-0.5">{r.report_type}</span><span className="text-[11px] text-slate-400">{r.date}</span></div>
                <div className="text-sm font-semibold text-slate-800 mt-1.5">{r.study_name}{r.body_site ? ` · ${r.body_site}` : ""}</div>
                {r.impression && <div className="text-sm text-slate-600 mt-1">{r.impression}</div>}
                {r.notes && <div className="text-xs text-slate-400 mt-1">{r.notes}</div>}
                {r.file_id && <Button variant="outline" size="sm" className="mt-2" onClick={() => viewIpdFile(r.file_id)} data-testid={`radiology-view-${r.id}`}><Eye className="h-3.5 w-3.5 mr-1.5" /> View {r.filename ? "" : "file"}</Button>}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="documents-section">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2 text-amber-700 font-bold"><FolderOpen className="h-5 w-5" /> OTHER REPORTS & DOCUMENTS</div>
          {editable && <Button size="sm" className="bg-blue-700 hover:bg-blue-800" onClick={() => setDocOpen(true)} data-testid="upload-report-btn"><Upload className="h-4 w-4 mr-1.5" /> Upload Report</Button>}
        </div>
        {documents.length === 0 ? (
          <div className="text-center py-10 text-slate-400"><FolderOpen className="h-8 w-8 mx-auto mb-2 opacity-40" />No documents uploaded yet. Click <b>Upload Report</b> to add culture reports, ECG, biopsy reports, etc.</div>
        ) : (
          <div className="space-y-2">
            {[...documents].reverse().map((d) => (
              <div key={d.id} className="flex items-center justify-between border border-slate-200 rounded-md px-4 py-2.5" data-testid={`document-item-${d.id}`}>
                <div><div className="text-sm font-medium text-slate-800">{d.report_name} <span className="text-xs font-normal text-amber-700">· {d.category}</span></div>{d.description && <div className="text-xs text-slate-500">{d.description}</div>}<div className="text-[11px] text-slate-400">{d.date}</div></div>
                {d.file_id && <Button variant="outline" size="sm" onClick={() => viewIpdFile(d.file_id)} data-testid={`document-view-${d.id}`}><Eye className="h-3.5 w-3.5 mr-1.5" /> View</Button>}
              </div>
            ))}
          </div>
        )}
      </div>

      <LabResultsModal open={labOpen} onOpenChange={setLabOpen} onSave={(data) => onEntry("lab_results", data)} busy={busy} extraParams={extraParams.map((p) => p.k)} />
      <RadiologyModal open={radOpen} onOpenChange={setRadOpen} onSave={(data) => onEntry("radiology", data)} busy={busy} />
      <DocumentModal open={docOpen} onOpenChange={setDocOpen} onSave={(data) => onEntry("documents", data)} busy={busy} />
    </div>
  );
}

function LabResultsModal({ open, onOpenChange, onSave, busy, extraParams = [] }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [values, setValues] = useState({});
  const [customParams, setCustomParams] = useState([]);
  const [newParam, setNewParam] = useState("");
  const [saving, setSaving] = useState(false);
  const knownNames = [...LAB_PANEL.map((p) => p.k), ...extraParams, ...customParams];
  const addParam = () => {
    const n = newParam.trim();
    if (!n) return;
    if (knownNames.some((x) => x.toLowerCase() === n.toLowerCase())) { toast.error(`"${n}" is already in the list`); return; }
    setCustomParams([...customParams, n]);
    setNewParam("");
  };
  const save = async () => {
    const filled = Object.fromEntries(Object.entries(values).filter(([, v]) => v !== "" && v != null));
    if (Object.keys(filled).length === 0) { toast.error("Enter at least one result"); return; }
    setSaving(true);
    if (await onSave({ date, values: filled })) { setValues({}); setCustomParams([]); setNewParam(""); onOpenChange(false); }
    setSaving(false);
  };
  const customRows = [...extraParams, ...customParams];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="enter-results-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><FlaskConical className="h-5 w-5 text-blue-600" /> Enter Investigation Results</DialogTitle>
          <DialogDescription>Record lab values for a date. Leave blank what wasn't tested. Add a custom investigation if it isn't in the list.</DialogDescription></DialogHeader>
        <div className="space-y-1.5"><Label className="text-xs">Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="lab-date-input" /></div>
        <div className="space-y-2 mt-2">
          {LAB_PANEL.map((p) => (
            <div key={p.k} className="grid grid-cols-[1fr_1.4fr] items-center gap-3">
              <Label className="text-sm text-slate-600" title={labRange(p)}>{p.k}</Label>
              <Input value={values[p.k] || ""} onChange={(e) => setValues({ ...values, [p.k]: e.target.value })} placeholder="value" data-testid={`lab-in-${p.k}`} />
            </div>
          ))}
          {customRows.map((name) => (
            <div key={name} className="grid grid-cols-[1fr_1.4fr] items-center gap-3">
              <Label className="text-sm text-slate-600 flex items-center gap-1">{name}<span className="text-[9px] font-semibold uppercase text-blue-500 bg-blue-50 rounded px-1">custom</span></Label>
              <Input value={values[name] || ""} onChange={(e) => setValues({ ...values, [name]: e.target.value })} placeholder="value" data-testid={`lab-in-${name}`} />
            </div>
          ))}
        </div>
        <div className="mt-3 border-t border-slate-100 pt-3">
          <Label className="text-xs text-slate-500">Add a custom investigation (not in the list)</Label>
          <div className="flex gap-2 mt-1.5">
            <Input value={newParam} onChange={(e) => setNewParam(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addParam(); } }} placeholder="e.g. D-Dimer, CRP, HbA1c" data-testid="lab-custom-name-input" />
            <Button type="button" variant="outline" onClick={addParam} data-testid="lab-custom-add-btn"><Plus className="h-4 w-4 mr-1.5" /> Add</Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving || busy} className="bg-blue-700 hover:bg-blue-800" data-testid="lab-save-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Results"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RadiologyModal({ open, onOpenChange, onSave, busy }) {
  const [f, setF] = useState({ report_type: RAD_TYPES[0], study_name: "", date: new Date().toISOString().slice(0, 10), body_site: "", impression: "", notes: "" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (!f.study_name.trim()) { toast.error("Study name is required"); return; }
    setSaving(true);
    try {
      let up = {};
      if (file) { const r = await uploadIpdFile(file); up = { file_id: r.file_id, filename: r.filename }; }
      if (await onSave({ ...f, ...up })) { onOpenChange(false); setF({ report_type: RAD_TYPES[0], study_name: "", date: new Date().toISOString().slice(0, 10), body_site: "", impression: "", notes: "" }); setFile(null); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="add-radiology-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Scan className="h-5 w-5 text-purple-600" /> Add Radiology Report</DialogTitle>
          <DialogDescription>Record an imaging study and optionally attach the report.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs">Report Type *</Label>
            <select className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white" value={f.report_type} onChange={set("report_type")} data-testid="rad-type-select">{RAD_TYPES.map((t) => <option key={t}>{t}</option>)}</select></div>
          <div className="space-y-1.5"><Label className="text-xs">Investigation / Study Name *</Label><Input value={f.study_name} onChange={set("study_name")} placeholder="e.g. CT Brain Plain" data-testid="rad-study-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">Date *</Label><Input type="date" value={f.date} onChange={set("date")} /></div>
            <div className="space-y-1.5"><Label className="text-xs">Body Site / Region</Label><Input value={f.body_site} onChange={set("body_site")} placeholder="e.g. Brain, Chest" /></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Short Impression</Label><Textarea rows={2} value={f.impression} onChange={set("impression")} placeholder="e.g. No acute intracranial abnormality" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Notes</Label><Textarea rows={2} value={f.notes} onChange={set("notes")} placeholder="Additional notes…" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Upload Report PDF / Image</Label>
            <label className="flex items-center justify-center gap-2 border border-dashed border-slate-300 rounded-md py-3 text-sm text-slate-500 cursor-pointer hover:bg-slate-50" data-testid="rad-file-label">
              <Upload className="h-4 w-4" /> {file ? file.name : "Click to select PDF or image"}
              <input type="file" accept=".pdf,image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="rad-file-input" />
            </label></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving || busy} className="bg-blue-700 hover:bg-blue-800" data-testid="rad-save-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Report"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DocumentModal({ open, onOpenChange, onSave, busy }) {
  const [f, setF] = useState({ category: DOC_CATEGORIES[0], report_name: "", date: new Date().toISOString().slice(0, 10), description: "" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (!f.report_name.trim()) { toast.error("Report name is required"); return; }
    setSaving(true);
    try {
      let up = {};
      if (file) { const r = await uploadIpdFile(file); up = { file_id: r.file_id, filename: r.filename }; }
      if (await onSave({ ...f, ...up })) { onOpenChange(false); setF({ category: DOC_CATEGORIES[0], report_name: "", date: new Date().toISOString().slice(0, 10), description: "" }); setFile(null); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="upload-report-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><FolderOpen className="h-5 w-5 text-amber-600" /> Upload Report / Document</DialogTitle>
          <DialogDescription>Attach culture reports, ECG, biopsy reports and other documents.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs">Category *</Label>
            <select className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white" value={f.category} onChange={set("category")} data-testid="doc-category-select">{DOC_CATEGORIES.map((t) => <option key={t}>{t}</option>)}</select></div>
          <div className="space-y-1.5"><Label className="text-xs">Report Name *</Label><Input value={f.report_name} onChange={set("report_name")} placeholder="e.g. Blood Culture — MRSA" data-testid="doc-name-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Date *</Label><Input type="date" value={f.date} onChange={set("date")} /></div>
          <div className="space-y-1.5"><Label className="text-xs">Description / Notes</Label><Textarea rows={2} value={f.description} onChange={set("description")} placeholder="Key findings, organism, sensitivity…" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Upload PDF</Label>
            <label className="flex items-center justify-center gap-2 border border-dashed border-slate-300 rounded-md py-3 text-sm text-slate-500 cursor-pointer hover:bg-slate-50" data-testid="doc-file-label">
              <Upload className="h-4 w-4" /> {file ? file.name : "Click to select PDF"}
              <input type="file" accept=".pdf,image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="doc-file-input" />
            </label></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving || busy} className="bg-blue-700 hover:bg-blue-800" data-testid="doc-save-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Report"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VitalsPanel({ vitals, adm, onAdd, editable, busy }) {
  const list = vitals || [];
  const historyCustom = [];
  list.forEach((e) => Object.keys(e).forEach((k) => { if (!RESERVED.has(k) && !historyCustom.includes(k)) historyCustom.push(k); }));
  const [customFields, setCustomFields] = useState([]);
  const [removed, setRemoved] = useState(new Set());
  const allCustom = [...new Set([...historyCustom, ...customFields])].filter((k) => !removed.has(k));

  const [form, setForm] = useState({});
  const setF = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [active, setActive] = useState(["hr", "sbp", "spo2"]);

  const seriesList = [
    ...STD_VITALS.filter((v) => TREND_KEYS.includes(v.key)),
    ...allCustom.map((k, i) => ({ key: k, label: k, trend: k, color: CUSTOM_PALETTE[i % CUSTOM_PALETTE.length] })),
  ];
  const toggle = (k) => setActive((a) => (a.includes(k) ? a.filter((x) => x !== k) : [...a, k]));

  const addCustom = () => {
    const key = slugKey(newName);
    if (!key) return;
    if (!customFields.includes(key)) setCustomFields([...customFields, key]);
    setRemoved((r) => { const n = new Set(r); n.delete(key); return n; });
    setNewName(""); setAdding(false);
  };
  const removeCustom = (k) => { setRemoved((r) => new Set(r).add(k)); setForm((f) => { const n = { ...f }; delete n[k]; return n; }); };

  const save = async () => {
    const payload = {};
    [...STD_KEYS, ...allCustom].forEach((k) => { if (form[k] !== undefined && form[k] !== "") payload[k] = form[k]; });
    if (Object.keys(payload).length === 0) { toast.error("Enter at least one vital value"); return; }
    if (await onAdd(payload)) setForm({});
  };

  const latest = list.length ? list[list.length - 1] : {};
  const chartData = list.map((e) => {
    const row = { time: new Date(e.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) };
    [...STD_KEYS, ...allCustom].forEach((k) => { const n = parseFloat(e[k]); if (!isNaN(n)) row[k] = n; });
    return row;
  });

  const printVitals = () => {
    const cols = [...STD_VITALS.map((v) => v.label), ...allCustom];
    const head = `<tr><th>Time</th>${cols.map((c) => `<th>${htmlEsc(c)}</th>`).join("")}</tr>`;
    const body = list.map((e) => `<tr><td>${new Date(e.at).toLocaleString()}</td>${STD_VITALS.map((v) => `<td style="text-align:center">${htmlEsc(e[v.key] ?? "—")}</td>`).join("")}${allCustom.map((k) => `<td style="text-align:center">${htmlEsc(e[k] ?? "—")}</td>`).join("")}</tr>`).join("") || `<tr><td colspan="${cols.length + 1}">No vitals recorded</td></tr>`;
    printSection(adm, "Vitals Chart", `<h3>Vitals Log</h3><table border="1" cellpadding="6" style="width:100%;border-collapse:collapse"><thead>${head}</thead><tbody>${body}</tbody></table>`);
  };

  return (
    <div className="space-y-5" data-testid="tab-panel-vitals">
      <div className="flex justify-end"><Button variant="outline" size="sm" onClick={printVitals} data-testid="print-vitals"><Printer className="h-4 w-4 mr-1.5" /> Print</Button></div>
      {editable && (
        <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="record-vitals-card">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2 text-slate-800 font-semibold"><Activity className="h-5 w-5 text-blue-600" /> Record Vitals</div>
            <Button variant="outline" size="sm" onClick={() => setAdding((a) => !a)} data-testid="add-vital-toggle"><Plus className="h-4 w-4 mr-1.5" /> Add Vital</Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {STD_VITALS.map((v) => (
              <div key={v.key} className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">{v.label}</Label>
                <Input value={form[v.key] || ""} onChange={setF(v.key)} placeholder={v.unit} data-testid={`vital-in-${v.key}`} /></div>
            ))}
          </div>
          {(allCustom.length > 0 || adding) && (
            <div className="border-t border-slate-100 mt-5 pt-4">
              <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-3">Custom Vitals</div>
              {adding && (
                <div className="flex gap-2 mb-3 max-w-sm">
                  <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Vital name (e.g. Blood Sugar)" onKeyDown={(e) => e.key === "Enter" && addCustom()} data-testid="new-vital-name" />
                  <Button size="sm" onClick={addCustom} className="bg-blue-700 hover:bg-blue-800" data-testid="new-vital-confirm">Add</Button>
                </div>
              )}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {allCustom.map((k) => (
                  <div key={k} className="space-y-1.5">
                    <div className="flex items-center justify-between"><Label className="text-xs font-semibold text-slate-600 truncate">{k}</Label>
                      <button onClick={() => removeCustom(k)} className="text-slate-300 hover:text-red-500" data-testid={`remove-vital-${k}`}><X className="h-3.5 w-3.5" /></button></div>
                    <Input value={form[k] || ""} onChange={setF(k)} placeholder="—" data-testid={`vital-in-${k}`} />
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="mt-5"><Button onClick={save} disabled={busy} className="bg-blue-700 hover:bg-blue-800" data-testid="save-vitals-button"><Activity className="h-4 w-4 mr-1.5" /> Save Vitals</Button></div>
        </div>
      )}

      {list.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[...STD_VITALS, ...allCustom.map((k, i) => ({ key: k, label: k, unit: "", color: CUSTOM_PALETTE[i % CUSTOM_PALETTE.length] }))].map((v) => (
            <div key={v.key} className="rounded-md border border-slate-200 bg-white p-3 text-center" data-testid={`vital-summary-${v.key}`}>
              <div className="text-sm font-semibold text-slate-700">{v.label}</div>
              <div className="h-1 w-8 mx-auto my-2 rounded-full" style={{ background: v.color }} />
              <div className={`text-lg font-bold ${cellClass(v.key, latest[v.key])}`}>{latest[v.key] ?? "—"}</div>
              <div className="text-[11px] text-slate-400">{v.unit}</div>
            </div>
          ))}
        </div>
      )}

      {list.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="vitals-trend-card">
          <div className="flex flex-wrap items-center gap-2 mb-5">
            <span className="font-semibold text-slate-800 mr-2">Trend Chart</span>
            {seriesList.map((s) => {
              const on = active.includes(s.key);
              return (
                <button key={s.key} onClick={() => toggle(s.key)} data-testid={`trend-toggle-${s.key}`}
                  className="px-3 py-1 rounded-full text-xs font-medium border transition-colors"
                  style={on ? { background: s.color, borderColor: s.color, color: "#fff" } : { borderColor: "#e2e8f0", color: "#64748b" }}>
                  {s.trend}
                </button>
              );
            })}
          </div>
          <div style={{ width: "100%", height: 320 }}>
            <ResponsiveContainer>
              <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="time" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} />
                {seriesList.filter((s) => active.includes(s.key)).map((s) => (
                  <Line key={s.key} type="monotone" dataKey={s.key} name={s.trend} stroke={s.color} strokeWidth={2} dot={{ r: 3 }} connectNulls />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="vitals-log-card">
        <div className="font-semibold text-slate-800 mb-4">Vitals Log</div>
        {list.length === 0 ? <div className="text-sm text-slate-400">No vitals recorded yet. Use "Record Vitals" above.</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-200 text-left text-slate-500">
                <th className="py-2 pr-4 font-medium whitespace-nowrap">Time</th>
                {STD_VITALS.map((v) => <th key={v.key} className="py-2 px-3 font-medium">{v.label}</th>)}
                {allCustom.map((k) => <th key={k} className="py-2 px-3 font-medium">{k}</th>)}
              </tr></thead>
              <tbody>
                {[...list].reverse().map((e) => (
                  <tr key={e.id} className="border-b border-slate-50" data-testid={`vitals-log-row-${e.id}`}>
                    <td className="py-2 pr-4 text-slate-500 whitespace-nowrap">{new Date(e.at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</td>
                    {STD_VITALS.map((v) => <td key={v.key} className={`py-2 px-3 ${cellClass(v.key, e[v.key])}`}>{e[v.key] || "—"}</td>)}
                    {allCustom.map((k) => <td key={k} className="py-2 px-3 text-slate-700">{e[k] || "—"}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function TwoFieldForm({ a, b, onAdd, busy }) {
  const [x, setX] = useState(""); const [y, setY] = useState("");
  return (
    <div className="flex flex-col sm:flex-row gap-2">
      <Input placeholder={a} value={x} onChange={(e) => setX(e.target.value)} data-testid="inv-name" />
      <Input placeholder={b} value={y} onChange={(e) => setY(e.target.value)} data-testid="inv-notes" />
      <Button size="sm" disabled={busy || !x.trim()} onClick={async () => { if (await onAdd(x.trim(), y.trim())) { setX(""); setY(""); } }} className="bg-blue-700 hover:bg-blue-800" data-testid="inv-add"><Plus className="h-4 w-4 mr-1.5" /> Add</Button>
    </div>
  );
}

const RX_CATEGORIES = [
  { k: "Injection", label: "Injections", color: "bg-purple-100 text-purple-700" },
  { k: "Oral / Tablets", label: "Oral / Tablets", color: "bg-emerald-100 text-emerald-700" },
  { k: "Nebulisers", label: "Nebulisers", color: "bg-amber-100 text-amber-700" },
  { k: "IV Fluids", label: "IV Fluids", color: "bg-blue-100 text-blue-700" },
  { k: "Syrup", label: "Syrup", color: "bg-pink-100 text-pink-700" },
  { k: "Topical", label: "Topical", color: "bg-slate-100 text-slate-600" },
  { k: "Other", label: "Other", color: "bg-slate-100 text-slate-600" },
];
const catMeta = (k) => RX_CATEGORIES.find((c) => c.k === k) || RX_CATEGORIES[RX_CATEGORIES.length - 1];
const RX_UNITS = ["mg", "g", "mL", "mcg", "IU", "tab", "drop", "puff", "%"];
const RX_ROUTES = ["IV", "IM", "PO", "SC", "Inhaled", "Topical", "SL", "PR", "NG"];
const FREQS = ["OD", "BD", "TDS", "QID", "Q6H", "HS", "STAT", "SOS"];
const FREQ_TIMES = { OD: ["08:00"], BD: ["08:00", "20:00"], TDS: ["08:00", "14:00", "20:00"], QID: ["06:00", "12:00", "18:00", "22:00"], Q6H: ["06:00", "12:00", "18:00", "00:00"], HS: ["22:00"], STAT: [], SOS: [] };
const TIME_OPTIONS = ["06:00", "08:00", "10:00", "12:00", "14:00", "16:00", "18:00", "20:00", "22:00", "00:00"];
const dstr = (d) => d.toISOString().slice(0, 10);
const orderActiveOn = (o, dateStr) => {
  const start = o.start_date || (o.at ? o.at.slice(0, 10) : null);
  if (start && start > dateStr) return false;
  if (o.stop_date && o.stop_date < dateStr) return false;
  return o.status !== "stopped";
};
const fmtSheetDate = (s) => { try { return new Date(s).toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" }); } catch { return s; } };

function TreatmentPanel({ adm, onEntry, editable, busy }) {
  const orders = adm.treatment || [];
  const [drugOptions, setDrugOptions] = useState([]);
  useEffect(() => {
    api.get("/ipd/drug-suggestions").then((r) => setDrugOptions(r.data || [])).catch(() => {});
  }, [adm.treatment]);
  const [date, setDate] = useState(dstr(new Date()));
  const [showForm, setShowForm] = useState(false);
  const [copying, setCopying] = useState(false);
  const shift = (delta) => { const d = new Date(date); d.setDate(d.getDate() + delta); setDate(dstr(d)); };
  const active = orders.filter((o) => orderActiveOn(o, date));
  const schedule = [];
  active.forEach((o) => (o.times || []).forEach((t) => schedule.push({ time: t, drug: o.drug, dose: o.dose, unit: o.unit, route: o.route })));
  schedule.sort((a, b) => a.time.localeCompare(b.time));

  const copyPrev = async () => {
    const prev = new Date(date); prev.setDate(prev.getDate() - 1);
    const prevStr = dstr(prev);
    const prevActive = orders.filter((o) => orderActiveOn(o, prevStr));
    if (prevActive.length === 0) { toast.error("No active orders on the previous day to copy"); return; }
    setCopying(true);
    try {
      for (const o of prevActive) {
        await onEntry({ category: o.category, drug: o.drug, dose: o.dose, unit: o.unit, route: o.route,
          frequency: o.frequency, times: o.times || [], instructions: o.instructions, prn: o.prn,
          start_date: date, stop_date: o.stop_date || "", status: "active", copied_from: prevStr });
      }
      toast.success(`Copied ${prevActive.length} order(s) to ${fmtSheetDate(date)}`);
    } finally { setCopying(false); }
  };

  const printSheet = () => {
    const rows = active.map((o) => `<tr><td>${o.category || ""}</td><td>${o.drug} ${o.dose || ""} ${o.unit || ""}</td><td style="text-align:center">${o.route || ""}</td><td style="text-align:center">${o.frequency || ""}</td><td style="text-align:center">${(o.times || []).join(", ") || "—"}</td></tr>`).join("");
    printDocument("Treatment Sheet", `<div class="row"><div><h1>${adm.patient?.name || ""}</h1><div class="muted">${adm.ipd_no} · Bed ${adm.bed_number} · ${adm.department || ""}</div></div><div class="muted" style="text-align:right">${fmtSheetDate(date)}</div></div><hr class="hr"/><h3>Medication Orders</h3><table border="1" cellpadding="6" style="width:100%;border-collapse:collapse"><thead><tr><th style="text-align:left">Category</th><th style="text-align:left">Drug / Dose</th><th>Route</th><th>Frequency</th><th>Times</th></tr></thead><tbody>${rows || '<tr><td colspan="5">No active orders</td></tr>'}</tbody></table>`);
  };

  return (
    <div className="space-y-5" data-testid="tab-panel-treatment">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-md px-2 h-10">
            <button onClick={() => shift(-1)} className="p-1 text-slate-500 hover:text-slate-800" data-testid="rx-prev-day"><ChevronLeft className="h-4 w-4" /></button>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="text-sm font-semibold text-slate-800 bg-transparent focus:outline-none" data-testid="rx-date-input" />
            <button onClick={() => shift(1)} className="p-1 text-slate-500 hover:text-slate-800" data-testid="rx-next-day"><ChevronRight className="h-4 w-4" /></button>
          </div>
          <Button size="sm" onClick={() => setDate(dstr(new Date()))} className="bg-blue-700 hover:bg-blue-800 h-10" data-testid="rx-today">Today</Button>
        </div>
        <div className="flex items-center gap-2">
          {editable && <Button variant="outline" onClick={copyPrev} disabled={copying} className="h-10" data-testid="rx-copy">{copying ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Copy className="h-4 w-4 mr-1.5" />} Copy</Button>}
          <Button variant="outline" onClick={printSheet} className="h-10" data-testid="rx-print"><Printer className="h-4 w-4 mr-1.5" /> Print</Button>
        </div>
      </div>

      <div className="rounded-md border border-blue-100 bg-blue-50/40 p-5" data-testid="rx-schedule-card">
        <div className="flex items-center gap-2 text-blue-700 font-semibold mb-3"><Clock className="h-5 w-5" /> Today's Medication Schedule</div>
        {schedule.length === 0 ? <div className="text-sm text-slate-400">No scheduled medications for this day.</div> : (
          <div className="space-y-2">
            {schedule.map((s, i) => (
              <div key={i} className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-md px-3 py-2 mr-2 text-sm" data-testid={`rx-sched-${i}`}>
                <span className="font-semibold text-blue-700">{s.time}</span><span className="text-slate-300">·</span>
                <span className="font-medium text-slate-800">{s.drug}</span>
                <span className="text-slate-400">{s.dose || ""} {s.unit || ""}</span><span className="text-slate-400">{s.route || ""}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {editable && (
        <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="rx-neworder-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-slate-800 font-semibold"><Plus className="h-5 w-5 text-blue-600" /> New Medication Order</div>
            {showForm
              ? <Button variant="outline" size="sm" onClick={() => setShowForm(false)} data-testid="rx-collapse"><ChevronDown className="h-4 w-4 mr-1.5" /> Collapse</Button>
              : <Button size="sm" className="bg-blue-700 hover:bg-blue-800" onClick={() => setShowForm(true)} data-testid="rx-add-order"><Plus className="h-4 w-4 mr-1.5" /> Add Order</Button>}
          </div>
          {showForm && <div className="mt-4 border-t border-slate-100 pt-4"><RxOrderForm defaultDate={date} busy={busy} drugOptions={drugOptions} onClose={() => setShowForm(false)} onAdd={async (data) => { const ok = await onEntry(data); return ok; }} /></div>}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="rx-orders-card">
        <div className="text-sm text-slate-500 mb-4">{active.length} active order{active.length === 1 ? "" : "s"}</div>
        {active.length === 0 ? <div className="text-sm text-slate-400">No active orders for this day. Use "Add Order" or "Copy" the previous day.</div> : (
          <div className="space-y-4">
            {active.map((o) => (
              <div key={o.id} className="border border-slate-100 rounded-md p-4" data-testid={`rx-order-${o.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`text-xs font-medium rounded px-2 py-0.5 ${catMeta(o.category).color}`}>{catMeta(o.category).label}</span>
                  <span className="font-semibold text-slate-800">{o.drug}</span>
                  <span className="text-[11px] font-medium text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5">{o.prn ? "PRN" : "ACTIVE"}</span>
                </div>
                <div className="text-sm mt-1"><span className="font-semibold text-slate-800">{o.dose} {o.unit}</span> <span className="text-slate-500 ml-1">{o.route}</span> <span className="text-blue-600 font-medium ml-1">{o.frequency}</span></div>
                <div className="text-[11px] text-slate-400 mt-0.5">{o.start_date || (o.at ? o.at.slice(0, 10) : "")}{o.instructions ? ` · ${o.instructions}` : ""}</div>
                {(o.times || []).length > 0 && <div className="flex flex-wrap gap-1.5 mt-2">{o.times.map((t) => <span key={t} className="text-xs bg-slate-100 text-slate-600 rounded px-2 py-0.5">{t}</span>)}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function RxOrderForm({ onAdd, onClose, busy, defaultDate, drugOptions = [] }) {
  const [f, setF] = useState({ category: "Injection", drug: "", dose: "", unit: "mg", route: "IV", frequency: "OD", start_date: defaultDate, stop_date: "", instructions: "", prn: false });
  const [times, setTimes] = useState(FREQ_TIMES.OD);
  const [addTime, setAddTime] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setFreq = (e) => { const v = e.target.value; setF({ ...f, frequency: v }); setTimes(FREQ_TIMES[v] || []); };
  const toggleTime = (t) => setTimes((ts) => (ts.includes(t) ? ts.filter((x) => x !== t) : [...ts, t].sort()));
  const save = async () => {
    if (!f.drug.trim()) { toast.error("Drug name is required"); return; }
    setSaving(true);
    const ok = await onAdd({ ...f, times, status: "active" });
    setSaving(false);
    if (ok) { setF({ ...f, drug: "", dose: "", instructions: "", prn: false }); onClose(); }
  };
  const sel = "w-full h-10 rounded-md border border-slate-300 px-3 text-sm bg-white";
  return (
    <div className="space-y-4">
      <div className="space-y-1.5"><Label className="text-xs">CATEGORY</Label>
        <select className={sel} value={f.category} onChange={set("category")} data-testid="rx-category">{RX_CATEGORIES.map((c) => <option key={c.k}>{c.k}</option>)}</select></div>
      <div className="space-y-1.5"><Label className="text-xs">DRUG NAME *</Label>
        <Input value={f.drug} onChange={set("drug")} list="rx-drug-suggestions" autoComplete="off" placeholder="Type initial letters to pick from history..." data-testid="rx-drug" />
        <datalist id="rx-drug-suggestions">{drugOptions.map((d) => <option key={d} value={d} />)}</datalist>
        {drugOptions.length > 0 && <p className="text-[10px] text-slate-400">{drugOptions.length} medicine{drugOptions.length === 1 ? "" : "s"} remembered — start typing to select.</p>}
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-1.5"><Label className="text-xs">DOSE</Label><Input value={f.dose} onChange={set("dose")} placeholder="e.g. 500" data-testid="rx-dose" /></div>
        <div className="space-y-1.5"><Label className="text-xs">UNIT</Label><select className={sel} value={f.unit} onChange={set("unit")} data-testid="rx-unit">{RX_UNITS.map((u) => <option key={u}>{u}</option>)}</select></div>
        <div className="space-y-1.5"><Label className="text-xs">ROUTE</Label><select className={sel} value={f.route} onChange={set("route")} data-testid="rx-route">{RX_ROUTES.map((r) => <option key={r}>{r}</option>)}</select></div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-1.5"><Label className="text-xs">FREQUENCY</Label><select className={sel} value={f.frequency} onChange={setFreq} data-testid="rx-frequency">{FREQS.map((q) => <option key={q}>{q}</option>)}</select></div>
        <div className="space-y-1.5"><Label className="text-xs">START DATE</Label><Input type="date" value={f.start_date} onChange={set("start_date")} data-testid="rx-start" /></div>
        <div className="space-y-1.5"><Label className="text-xs">STOP DATE</Label><Input type="date" value={f.stop_date} onChange={set("stop_date")} data-testid="rx-stop" /></div>
      </div>
      <div>
        <Label className="text-xs">SCHEDULED TIMES</Label>
        <div className="flex flex-wrap gap-2 mt-1.5">
          {TIME_OPTIONS.map((t) => (
            <button key={t} onClick={() => toggleTime(t)} data-testid={`rx-time-${t}`}
              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${times.includes(t) ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>{t}</button>
          ))}
          <div className="flex items-center gap-1">
            <select className="h-9 rounded-md border border-slate-300 px-2 text-sm bg-white" value={addTime} onChange={(e) => setAddTime(e.target.value)}>
              <option value="">--:--</option>
              {["01:00", "02:00", "03:00", "04:00", "05:00", "07:00", "09:00", "11:00", "13:00", "15:00", "17:00", "19:00", "21:00", "23:00"].map((t) => <option key={t}>{t}</option>)}
            </select>
            <Button variant="outline" size="sm" disabled={!addTime} onClick={() => { toggleTime(addTime); setAddTime(""); }} data-testid="rx-time-add">Add</Button>
          </div>
        </div>
      </div>
      <div className="space-y-1.5"><Label className="text-xs">SPECIAL INSTRUCTIONS</Label><Input value={f.instructions} onChange={set("instructions")} placeholder="Optional — e.g. give with food, dilute in 100 mL" data-testid="rx-instructions" /></div>
      <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={f.prn} onChange={(e) => setF({ ...f, prn: e.target.checked })} data-testid="rx-prn" /> PRN / SOS</label>
      <div className="flex gap-2 pt-2">
        <Button onClick={save} disabled={saving || busy || !f.drug.trim()} className="bg-blue-700 hover:bg-blue-800" data-testid="rx-add-to-sheet">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1.5" /> Add to Sheet</>}</Button>
        <Button variant="outline" onClick={onClose} data-testid="rx-cancel">Cancel</Button>
      </div>
    </div>
  );
}

function InvoiceStatusBadge({ status }) {
  const map = {
    settled: { c: "bg-emerald-100 text-emerald-700", t: "Settled" },
    pending: { c: "bg-amber-100 text-amber-700", t: "Pending" },
    open: { c: "bg-slate-100 text-slate-500", t: "Draft" },
  };
  const s = map[status] || map.open;
  return <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${s.c}`} data-testid="invoice-status-badge">{s.t}</span>;
}

function DiscountForm({ current, onSave, busy }) {
  const [amount, setAmount] = useState(current || "");
  return (
    <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
      <div className="space-y-1 sm:w-40"><Label className="text-xs">Discount ₹ (flat)</Label><Input type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="discount-amount" /></div>
      <Button size="sm" disabled={busy} onClick={async () => { await onSave(Math.max(0, parseFloat(amount) || 0)); }} className="bg-blue-700 hover:bg-blue-800" data-testid="discount-save"><Plus className="h-4 w-4 mr-1.5" /> Apply discount</Button>
    </div>
  );
}

function printInvoice(adm) {
  const inv = adm.invoice || {};
  const cats = Object.entries(inv.category_totals || {}).map(([c, v]) => `<tr><td>${c}</td><td style="text-align:right">₹${Number(v).toLocaleString()}</td></tr>`).join("");
  const consultants = (inv.charges || adm.charges || []).filter((c) => c.kind === "consultant");
  const consultantRows = consultants.length ? `<tr><td colspan="2" style="font-size:11px;color:#64748b">Consultant visits:</td></tr>` + consultants.map((c) => `<tr><td style="padding-left:16px">${c.consultant_name || ""}</td><td style="text-align:right">₹${Number(c.amount || 0).toLocaleString()}</td></tr>`).join("") : "";
  const discRow = Number(inv.discount || 0) > 0 ? `<tr><td>Discount</td><td style="text-align:right">− ₹${Number(inv.discount).toLocaleString()}</td></tr>` : "";
  printDocument("Inpatient Invoice", `
    <div class="row"><div><h1>${adm.patient?.name || ""}</h1><div class="muted">${adm.ipd_no} · Bed ${adm.bed_number} · ${adm.department || ""}</div></div>
    <div class="muted" style="text-align:right"><b>${inv.invoice_no || ""}</b><br/>${inv.generated_at ? new Date(inv.generated_at).toLocaleDateString() : ""}</div></div>
    <hr class="hr"/>
    <table style="width:100%;border-collapse:collapse" border="1" cellpadding="6"><thead><tr><th style="text-align:left">Category</th><th style="text-align:right">Amount</th></tr></thead>
    <tbody>${cats}${consultantRows}${discRow}<tr><td><b>Total</b></td><td style="text-align:right"><b>₹${Number(inv.total || 0).toLocaleString()}</b></td></tr>
    <tr><td>Paid</td><td style="text-align:right">₹${Number(inv.paid || 0).toLocaleString()}</td></tr>
    <tr><td><b>Balance Due</b></td><td style="text-align:right"><b>₹${Number(inv.balance || 0).toLocaleString()}</b></td></tr></tbody></table>`);
}

const OT_CHECKLIST = [
  ["nbm", "NBM form (Nil By Mouth)"],
  ["consents", "Consents"],
  ["id_band", "Identification band"],
  ["ornaments", "Removal of ornaments"],
  ["mouth_care", "Mouth care"],
  ["parts_prep", "Preparation of parts"],
  ["investigations", "Investigations"],
  ["blood_prep", "Preparation of blood"],
  ["medicines", "Medicines ordered"],
];
const ANAES_TYPES = ["General", "Spinal", "Local", "Regional", "Sedation"];

function OTCard({ title, children, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-6" data-testid={testid}>
      <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-4">{title}</div>
      {children}
    </div>
  );
}

function OTPanel({ ot, doctors, editable, busy, onSave }) {
  const src = ot || {};
  const [plan, setPlan] = useState(() => ({ procedure: "", anaesthesia: "General", date: "", time: "", surgeon: "", status: "planned", ...(src.plan || {}) }));
  const [check, setCheck] = useState(() => ({ ...(src.checklist || {}) }));
  const [anaes, setAnaes] = useState(() => ({ premedication: "", induction: "", maintenance: "", recovery: "", ...(src.anaesthetist || {}) }));
  const [op, setOp] = useState(() => ({ operation_name: "", in_time: "", out_time: "", surgeon_team: "", anaesthetist_names: "", staff_name: "", ...(src.operative || {}) }));

  const inputCls = "w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white";
  const done = OT_CHECKLIST.filter(([k]) => check[k]).length;

  return (
    <div className="space-y-5 max-w-3xl" data-testid="tab-panel-ot">
      {/* 1. Plan */}
      <OTCard title="OT Plan" testid="ot-plan-card">
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm text-slate-500">Schedule the operative procedure.</span>
          <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${plan.status === "confirmed" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid="ot-plan-status">{plan.status === "confirmed" ? "Confirmed" : "Planned"}</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1 md:col-span-2"><Label className="text-xs">Planned procedure</Label><Input value={plan.procedure} onChange={(e) => setPlan({ ...plan, procedure: e.target.value })} placeholder="e.g. Laparoscopic Cholecystectomy" data-testid="ot-plan-procedure" /></div>
          <div className="space-y-1"><Label className="text-xs">Anaesthesia</Label>
            <select className={inputCls} value={plan.anaesthesia} onChange={(e) => setPlan({ ...plan, anaesthesia: e.target.value })} data-testid="ot-plan-anaesthesia">{ANAES_TYPES.map((a) => <option key={a}>{a}</option>)}</select></div>
          <div className="space-y-1"><Label className="text-xs">Surgeon</Label>
            <select className={inputCls} value={plan.surgeon} onChange={(e) => setPlan({ ...plan, surgeon: e.target.value })} data-testid="ot-plan-surgeon">
              <option value="">Select surgeon…</option>
              {(doctors || []).map((d) => <option key={d.id} value={d.name}>{d.designation ? `${d.name} (${d.designation})` : d.name}</option>)}
            </select></div>
          <div className="space-y-1"><Label className="text-xs">Date</Label><Input type="date" value={plan.date} onChange={(e) => setPlan({ ...plan, date: e.target.value })} data-testid="ot-plan-date" /></div>
          <div className="space-y-1"><Label className="text-xs">Time</Label><Input type="time" value={plan.time} onChange={(e) => setPlan({ ...plan, time: e.target.value })} data-testid="ot-plan-time" /></div>
        </div>
        {editable && (
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" size="sm" disabled={busy} onClick={() => onSave("plan", { ...plan, status: "planned" })} data-testid="ot-plan-save">Save Plan</Button>
            <Button size="sm" disabled={busy || !plan.date || !plan.time || !plan.surgeon} onClick={() => { setPlan({ ...plan, status: "confirmed" }); onSave("plan", { ...plan, status: "confirmed" }); }} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ot-plan-confirm"><CheckCircle2 className="h-4 w-4 mr-1.5" /> Confirm OT</Button>
          </div>
        )}
      </OTCard>

      {/* 2. Pre-operative checklist */}
      <OTCard title={`Pre-operative Preparation Checklist (${done}/${OT_CHECKLIST.length})`} testid="ot-checklist-card">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {OT_CHECKLIST.map(([k, label]) => (
            <label key={k} className={`flex items-center gap-2.5 rounded-md border px-3 py-2 text-sm cursor-pointer ${check[k] ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-white border-slate-200 text-slate-600"} ${editable ? "" : "pointer-events-none opacity-90"}`} data-testid={`ot-check-${k}`}>
              <input type="checkbox" checked={!!check[k]} onChange={(e) => setCheck({ ...check, [k]: e.target.checked })} disabled={!editable} className="h-4 w-4 accent-emerald-600" />
              {label}
            </label>
          ))}
        </div>
        {editable && <div className="flex justify-end mt-4"><Button size="sm" disabled={busy} onClick={() => onSave("checklist", check)} className="bg-blue-700 hover:bg-blue-800" data-testid="ot-checklist-save">Save Checklist</Button></div>}
      </OTCard>

      {/* 3. Anaesthetist note */}
      <OTCard title="Anaesthetist Note" testid="ot-anaesthetist-card">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[["premedication", "Pre-medication"], ["induction", "Induction"], ["maintenance", "Maintenance"], ["recovery", "Recovery"]].map(([k, label]) => (
            <div key={k} className="space-y-1"><Label className="text-xs">{label}</Label><Textarea rows={3} value={anaes[k]} onChange={(e) => setAnaes({ ...anaes, [k]: e.target.value })} disabled={!editable} data-testid={`ot-anaes-${k}`} /></div>
          ))}
        </div>
        {editable && <div className="flex justify-end mt-4"><Button size="sm" disabled={busy} onClick={() => onSave("anaesthetist", anaes)} className="bg-blue-700 hover:bg-blue-800" data-testid="ot-anaes-save">Save Anaesthetist Note</Button></div>}
        {src.anaesthetist?.updated_by && <div className="text-[11px] text-slate-400 mt-2">Last updated by {src.anaesthetist.updated_by}</div>}
      </OTCard>

      {/* 4. Operative notes */}
      <OTCard title="Operative Notes" testid="ot-operative-card">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1 md:col-span-2"><Label className="text-xs">Operation name</Label><Input value={op.operation_name} onChange={(e) => setOp({ ...op, operation_name: e.target.value })} disabled={!editable} data-testid="ot-op-name" /></div>
          <div className="space-y-1"><Label className="text-xs">In time</Label><Input type="time" value={op.in_time} onChange={(e) => setOp({ ...op, in_time: e.target.value })} disabled={!editable} data-testid="ot-op-in" /></div>
          <div className="space-y-1"><Label className="text-xs">Out time</Label><Input type="time" value={op.out_time} onChange={(e) => setOp({ ...op, out_time: e.target.value })} disabled={!editable} data-testid="ot-op-out" /></div>
          <div className="space-y-1 md:col-span-2"><Label className="text-xs">Surgeon team names</Label><Textarea rows={2} value={op.surgeon_team} onChange={(e) => setOp({ ...op, surgeon_team: e.target.value })} disabled={!editable} data-testid="ot-op-surgeons" /></div>
          <div className="space-y-1"><Label className="text-xs">Anaesthetist names</Label><Input value={op.anaesthetist_names} onChange={(e) => setOp({ ...op, anaesthetist_names: e.target.value })} disabled={!editable} data-testid="ot-op-anaesthetists" /></div>
          <div className="space-y-1"><Label className="text-xs">Staff name(s)</Label><Input value={op.staff_name} onChange={(e) => setOp({ ...op, staff_name: e.target.value })} disabled={!editable} data-testid="ot-op-staff" /></div>
        </div>
        {editable && <div className="flex justify-end mt-4"><Button size="sm" disabled={busy} onClick={() => onSave("operative", op)} className="bg-blue-700 hover:bg-blue-800" data-testid="ot-operative-save">Save Operative Notes</Button></div>}
      </OTCard>
    </div>
  );
}

function RowEditor({ label, columns, rows, setRows, editable, testid }) {
  const blank = Object.fromEntries(columns.map((c) => [c.k, ""]));
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <Label className="text-sm text-slate-700">{label}</Label>
        {editable && <Button type="button" size="sm" variant="outline" onClick={() => setRows([...(rows || []), { ...blank }])} data-testid={`${testid}-add`}><Plus className="h-3.5 w-3.5 mr-1" /> Add</Button>}
      </div>
      {(rows || []).length === 0 ? <div className="text-xs text-slate-400 mb-2">None recorded.</div> : (
        <div className="space-y-2">
          {(rows || []).map((r, i) => (
            <div key={i} className="flex items-center gap-2">
              {columns.map((c) => (
                c.type === "date"
                  ? <Input key={c.k} type="date" value={r[c.k] || ""} disabled={!editable} onChange={(e) => { const n = [...rows]; n[i] = { ...n[i], [c.k]: e.target.value }; setRows(n); }} className="flex-1" data-testid={`${testid}-${i}-${c.k}`} />
                  : <Input key={c.k} value={r[c.k] || ""} disabled={!editable} placeholder={c.ph} onChange={(e) => { const n = [...rows]; n[i] = { ...n[i], [c.k]: e.target.value }; setRows(n); }} className="flex-1" data-testid={`${testid}-${i}-${c.k}`} />
              ))}
              {editable && <button type="button" onClick={() => setRows(rows.filter((_, j) => j !== i))} className="text-slate-300 hover:text-red-500" data-testid={`${testid}-${i}-del`}><Trash2 className="h-4 w-4" /></button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PatientHistoryPanel({ history, adm, editable, busy, onSave }) {
  const h = history || {};
  const [knownDiseases, setKnownDiseases] = useState(() => h.known_diseases || []);
  const [pastOperations, setPastOperations] = useState(() => h.past_operations || []);
  const [allergies, setAllergies] = useState(() => h.allergies || "");
  const [familyHistory, setFamilyHistory] = useState(() => h.family_history || "");
  const [personalHistory, setPersonalHistory] = useState(() => h.personal_history || "");
  const [pastHospitalisations, setPastHospitalisations] = useState(() => h.past_hospitalisations || []);
  const submit = () => onSave({ known_diseases: knownDiseases, past_operations: pastOperations, allergies, family_history: familyHistory, personal_history: personalHistory, past_hospitalisations: pastHospitalisations });
  const printHistory = () => {
    const li = (arr, f) => ((arr || []).map(f).join("") || "<li>—</li>");
    printSection(adm, "Patient History",
      `<div class="sec"><h3>Known diseases</h3><ul>${li(knownDiseases, (d) => `<li>${htmlEsc(d.name)}${d.since ? ` — since ${htmlEsc(d.since)}` : ""}</li>`)}</ul></div>
       <div class="sec"><h3>Past operations</h3><ul>${li(pastOperations, (d) => `<li>${htmlEsc(d.name)}${d.date ? ` — ${htmlEsc(d.date)}` : ""}</li>`)}</ul></div>
       <div class="sec"><h3>Allergies</h3>${nlBr(allergies) || "—"}</div>
       <div class="sec"><h3>Family history</h3>${nlBr(familyHistory) || "—"}</div>
       <div class="sec"><h3>Personal history</h3>${nlBr(personalHistory) || "—"}</div>
       <div class="sec"><h3>Past hospitalisations</h3><ul>${li(pastHospitalisations, (d) => `<li>${htmlEsc(d.diagnosis)}${d.time ? ` — ${htmlEsc(d.time)}` : ""}</li>`)}</ul></div>`);
  };
  return (
    <div className="space-y-5 max-w-3xl" data-testid="tab-panel-history">
      <div className="flex justify-end"><Button variant="outline" size="sm" onClick={printHistory} data-testid="print-history"><Printer className="h-4 w-4 mr-1.5" /> Print</Button></div>
      <div className="rounded-md border border-blue-100 bg-blue-50 px-4 py-2.5 text-xs text-blue-700">This is the patient's permanent history — it is saved to the patient and carries over to all future admissions.</div>
      <div className="bg-white border border-slate-200 rounded-md p-6 space-y-6">
        <RowEditor label="Known diseases" testid="hist-known-diseases" editable={editable} rows={knownDiseases} setRows={setKnownDiseases}
          columns={[{ k: "name", ph: "Disease / condition" }, { k: "since", ph: "Since (e.g. 2019)" }]} />
        <RowEditor label="Past operations" testid="hist-past-operations" editable={editable} rows={pastOperations} setRows={setPastOperations}
          columns={[{ k: "name", ph: "Operation" }, { k: "date", type: "date" }]} />
        <div className="space-y-1.5"><Label className="text-sm text-slate-700">Allergies</Label><Textarea rows={2} value={allergies} disabled={!editable} onChange={(e) => setAllergies(e.target.value)} placeholder="Drug / food / other allergies" data-testid="hist-allergies" /></div>
        <div className="space-y-1.5"><Label className="text-sm text-slate-700">Family history</Label><Textarea rows={2} value={familyHistory} disabled={!editable} onChange={(e) => setFamilyHistory(e.target.value)} data-testid="hist-family" /></div>
        <div className="space-y-1.5"><Label className="text-sm text-slate-700">Personal history</Label><Textarea rows={2} value={personalHistory} disabled={!editable} onChange={(e) => setPersonalHistory(e.target.value)} placeholder="Smoking, alcohol, diet, occupation…" data-testid="hist-personal" /></div>
        <RowEditor label="Past hospitalisations" testid="hist-past-hospitalisations" editable={editable} rows={pastHospitalisations} setRows={setPastHospitalisations}
          columns={[{ k: "diagnosis", ph: "Diagnosis" }, { k: "time", ph: "When (e.g. Jan 2023)" }]} />
        {editable && <div className="flex justify-end"><Button onClick={submit} disabled={busy} className="bg-blue-700 hover:bg-blue-800" data-testid="hist-save">Save Patient History</Button></div>}
        {h.updated_by && <div className="text-[11px] text-slate-400">Last updated by {h.updated_by}</div>}
      </div>
    </div>
  );
}

function PresentAssessmentPanel({ assessment, adm, editable, busy, onSave }) {
  const a = assessment || {};
  const [presentingComplaints, setPresentingComplaints] = useState(() => a.presenting_complaints || "");
  const [signs, setSigns] = useState(() => a.signs || "");
  const [examination, setExamination] = useState(() => a.examination || "");
  const [presentMedicines, setPresentMedicines] = useState(() => a.present_medicines || "");
  const submit = () => onSave({ presenting_complaints: presentingComplaints, signs, examination, present_medicines: presentMedicines });
  const printAssessment = () => printSection(adm, "Present Assessment",
    `<div class="sec"><h3>Presenting complaints</h3>${nlBr(presentingComplaints) || "—"}</div>
     <div class="sec"><h3>Signs</h3>${nlBr(signs) || "—"}</div>
     <div class="sec"><h3>Examination</h3>${nlBr(examination) || "—"}</div>
     <div class="sec"><h3>Present medicines</h3>${nlBr(presentMedicines) || "—"}</div>`);
  return (
    <div className="space-y-5 max-w-3xl" data-testid="tab-panel-assessment">
      <div className="bg-white border border-slate-200 rounded-md p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400">Present Assessment (this admission)</div>
          <Button variant="outline" size="sm" onClick={printAssessment} data-testid="print-assessment"><Printer className="h-4 w-4 mr-1.5" /> Print</Button>
        </div>
        <div className="space-y-1.5"><Label className="text-sm text-slate-700">Presenting complaints</Label><Textarea rows={3} value={presentingComplaints} disabled={!editable} onChange={(e) => setPresentingComplaints(e.target.value)} placeholder="Chief complaints on admission…" data-testid="assess-complaints" /></div>
        <div className="space-y-1.5"><Label className="text-sm text-slate-700">Signs</Label><Textarea rows={3} value={signs} disabled={!editable} onChange={(e) => setSigns(e.target.value)} data-testid="assess-signs" /></div>
        <div className="space-y-1.5"><Label className="text-sm text-slate-700">Examination</Label><Textarea rows={3} value={examination} disabled={!editable} onChange={(e) => setExamination(e.target.value)} placeholder="General & systemic examination findings…" data-testid="assess-examination" /></div>
        <div className="space-y-1.5"><Label className="text-sm text-slate-700">Present medicines</Label><Textarea rows={3} value={presentMedicines} disabled={!editable} onChange={(e) => setPresentMedicines(e.target.value)} placeholder="Current / ongoing medications…" data-testid="assess-medicines" /></div>
        {editable && <div className="flex justify-end"><Button onClick={submit} disabled={busy} className="bg-blue-700 hover:bg-blue-800" data-testid="assess-save">Save Present Assessment</Button></div>}
        {a.updated_by && <div className="text-[11px] text-slate-400">Last updated by {a.updated_by}</div>}
      </div>
    </div>
  );
}

function ConsultantVisitForm({ doctors, onAdd, busy }) {
  const [id, setId] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const consultant = (doctors || []).find((d) => d.id === id);
  return (
    <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
      <div className="flex-1 space-y-1"><Label className="text-xs">Consultant</Label>
        <select className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white" value={id} onChange={(e) => setId(e.target.value)} data-testid="consultant-select">
          <option value="">Select consultant…</option>
          {(doctors || []).map((d) => <option key={d.id} value={d.id}>{d.designation ? `${d.name} (${d.designation})` : d.name}</option>)}
        </select></div>
      <div className="space-y-1 sm:w-32"><Label className="text-xs">Charge ₹</Label><Input type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="consultant-charge" /></div>
      <div className="flex-1 space-y-1"><Label className="text-xs">Note</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Morning round" data-testid="consultant-note" /></div>
      <Button size="sm" disabled={busy || !consultant || !amount} onClick={async () => { if (await onAdd(consultant, parseFloat(amount), note)) { setId(""); setAmount(""); setNote(""); } }} className="bg-blue-700 hover:bg-blue-800" data-testid="consultant-add"><Plus className="h-4 w-4 mr-1.5" /> Add visit</Button>
    </div>
  );
}

function PaymentForm({ onAdd, busy }) {
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("Cash");
  const [note, setNote] = useState("");
  return (
    <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
      <div className="space-y-1 sm:w-36"><Label className="text-xs">Amount ₹</Label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="payment-amount" /></div>
      <div className="space-y-1 sm:w-36"><Label className="text-xs">Mode</Label>
        <select className="w-full h-9 rounded-md border border-slate-300 px-2 text-sm bg-white" value={mode} onChange={(e) => setMode(e.target.value)} data-testid="payment-mode">
          {["Cash", "Card", "UPI", "Bank Transfer", "Insurance"].map((m) => <option key={m}>{m}</option>)}
        </select></div>
      <div className="flex-1 space-y-1"><Label className="text-xs">Note</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Advance at admission" data-testid="payment-note" /></div>
      <Button size="sm" disabled={busy || !amount} onClick={async () => { if (await onAdd(parseFloat(amount), mode, note)) { setAmount(""); setNote(""); } }} className="bg-emerald-600 hover:bg-emerald-700" data-testid="payment-add"><Plus className="h-4 w-4 mr-1.5" /> Record payment</Button>
    </div>
  );
}

function CatalogChargeForm({ catalog, onAdd, busy }) {
  const [id, setId] = useState("");
  const [qty, setQty] = useState(1);
  if (!catalog || catalog.length === 0)
    return <div className="text-sm text-slate-400">No charges in the hospital charge list yet. Ask an admin to add them under <b>Charge Management</b>.</div>;
  const item = catalog.find((c) => c.id === id);
  return (
    <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
      <div className="flex-1 space-y-1"><Label className="text-xs">Charge</Label>
        <select className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white" value={id} onChange={(e) => setId(e.target.value)} data-testid="charge-catalog-select">
          <option value="">Select a charge…</option>
          {catalog.map((c) => <option key={c.id} value={c.id}>{`${c.name} — ₹${c.amount} (${c.category})`}</option>)}
        </select></div>
      <div className="space-y-1 sm:w-24"><Label className="text-xs">Qty</Label><Input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="charge-qty" /></div>
      <Button size="sm" disabled={busy || !item} onClick={async () => { if (await onAdd(item, Math.max(1, parseInt(qty) || 1))) { setId(""); setQty(1); } }} className="bg-blue-700 hover:bg-blue-800" data-testid="charge-add"><Plus className="h-4 w-4 mr-1.5" /> Add to bill</Button>
    </div>
  );
}

function CatalogChargeRow({ c, canEdit, onEdit, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState({ description: c.description || "", category: c.category || "", amount: c.amount || 0 });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    const ok = await onEdit(c.id, { description: f.description, category: f.category, amount: Number(f.amount) });
    setSaving(false);
    if (ok) setEditing(false);
  };
  if (editing) return (
    <TableRow data-testid={`charge-item-${c.id}`}>
      <TableCell colSpan={2}>
        <div className="flex flex-wrap items-center gap-2" data-testid={`charge-edit-${c.id}`}>
          <Input className="h-8 flex-1 min-w-[140px]" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Description" data-testid={`charge-edit-desc-${c.id}`} />
          <Input className="h-8 w-32" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} placeholder="Category" data-testid={`charge-edit-cat-${c.id}`} />
          <Input className="h-8 w-24" type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} placeholder="₹" data-testid={`charge-edit-amt-${c.id}`} />
          <Button size="sm" onClick={save} disabled={saving} className="h-8 bg-emerald-600 hover:bg-emerald-700" data-testid={`charge-save-${c.id}`}>{saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}</Button>
          <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="h-8" data-testid={`charge-cancel-${c.id}`}><X className="h-3.5 w-3.5" /></Button>
        </div>
      </TableCell>
    </TableRow>
  );
  return (
    <TableRow data-testid={`charge-item-${c.id}`}>
      <TableCell className="text-sm">{c.description}{c.category ? <span className="text-xs text-slate-400 ml-1">· {c.category}</span> : ""}<span className="text-[11px] text-slate-400 ml-2">{c.by}</span>{c.edited_at && <span className="text-[10px] text-amber-600 ml-1">(edited)</span>}</TableCell>
      <TableCell className="text-right text-sm whitespace-nowrap">₹{Number(c.amount || 0).toLocaleString()}
        {canEdit && (
          <span className="inline-flex gap-0.5 ml-2 align-middle">
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditing(true)} data-testid={`charge-edit-btn-${c.id}`}><Pencil className="h-3.5 w-3.5 text-slate-500" /></Button>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => onDelete(c)} data-testid={`charge-delete-btn-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
          </span>
        )}
      </TableCell>
    </TableRow>
  );
}

function ConsultantChargeRow({ c, canEdit, onEdit, onDelete }) {
  const note0 = (c.description || "").replace(`${c.consultant_name} — `, "");
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState({ note: note0, amount: c.amount || 0 });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    const desc = `${c.consultant_name}${f.note ? ` — ${f.note}` : " — consultant visit"}`;
    const ok = await onEdit(c.id, { description: desc, amount: Number(f.amount) });
    setSaving(false);
    if (ok) setEditing(false);
  };
  if (editing) return (
    <TableRow data-testid={`consultant-visit-${c.id}`}>
      <TableCell colSpan={4}>
        <div className="flex flex-wrap items-center gap-2" data-testid={`consultant-edit-${c.id}`}>
          <span className="text-sm font-medium text-slate-700">{c.consultant_name}</span>
          <Input className="h-8 flex-1 min-w-[140px]" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Note" data-testid={`consultant-edit-note-${c.id}`} />
          <Input className="h-8 w-24" type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} placeholder="₹" data-testid={`consultant-edit-amt-${c.id}`} />
          <Button size="sm" onClick={save} disabled={saving} className="h-8 bg-emerald-600 hover:bg-emerald-700" data-testid={`consultant-save-${c.id}`}>{saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}</Button>
          <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="h-8" data-testid={`consultant-cancel-${c.id}`}><X className="h-3.5 w-3.5" /></Button>
        </div>
      </TableCell>
    </TableRow>
  );
  return (
    <TableRow data-testid={`consultant-visit-${c.id}`}>
      <TableCell className="text-sm text-slate-600">{c.at ? new Date(c.at).toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" }) : "—"}</TableCell>
      <TableCell className="font-medium text-slate-800">{c.consultant_name}</TableCell>
      <TableCell className="text-sm text-slate-500">{note0 || "—"}{c.edited_at && <span className="text-[10px] text-amber-600 ml-1">(edited)</span>}</TableCell>
      <TableCell className="text-right text-sm whitespace-nowrap">₹{Number(c.amount || 0).toLocaleString()}
        {canEdit && (
          <span className="inline-flex gap-0.5 ml-2 align-middle">
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditing(true)} data-testid={`consultant-edit-btn-${c.id}`}><Pencil className="h-3.5 w-3.5 text-slate-500" /></Button>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => onDelete(c)} data-testid={`consultant-delete-btn-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
          </span>
        )}
      </TableCell>
    </TableRow>
  );
}

function DischargeForm({ onDischarge, editable, busy, admissionId, facilityId }) {
  const [s, setS] = useState("");
  const [streaming, setStreaming] = useState(false);
  const draft = async () => {
    setStreaming(true); setS("");
    try { await streamAI(`/ai/discharge-draft/${admissionId}`, { facilityId, onChunk: (t) => setS((v) => v + t) }); }
    catch { toast.error("AI draft failed. Please try again."); }
    finally { setStreaming(false); }
  };
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Label>Discharge summary</Label>
        {editable && (
          <Button type="button" size="sm" onClick={draft} disabled={streaming} className="bg-indigo-600 hover:bg-indigo-700" data-testid="discharge-ai-draft">
            {streaming ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Drafting…</> : <><Sparkles className="h-4 w-4 mr-1.5" /> Draft with AI</>}
          </Button>
        )}
      </div>
      <Textarea rows={8} value={s} onChange={(e) => setS(e.target.value)} placeholder="Condition at discharge, advice, follow-up… or tap 'Draft with AI' to generate from the admission record" data-testid="discharge-summary" />
      {s && !streaming && <p className="text-[11px] text-indigo-600">AI-generated draft — review and edit before discharging.</p>}
      {editable && <div className="flex justify-end"><Button disabled={busy} onClick={() => onDischarge(s)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="discharge-confirm"><LogOut className="h-4 w-4 mr-1.5" /> Discharge patient</Button></div>}
    </div>
  );
}

function ChatForm({ onSend, busy }) {
  const [t, setT] = useState("");
  return (
    <div className="flex gap-2">
      <Input value={t} onChange={(e) => setT(e.target.value)} placeholder="Message the care team…" onKeyDown={(e) => e.key === "Enter" && t.trim() && (async () => { if (await onSend(t.trim())) setT(""); })()} data-testid="chat-input" />
      <Button disabled={busy || !t.trim()} onClick={async () => { if (await onSend(t.trim())) setT(""); }} className="bg-blue-700 hover:bg-blue-800" data-testid="chat-send"><Send className="h-4 w-4" /></Button>
    </div>
  );
}
